/**
 * API service for backend communication with secure cookie authentication
 */
import axios, { AxiosInstance, AxiosError } from 'axios';
import apiErrorEmitter from './apiErrorEmitter';

// Determine API base URL.
// Runtime env.js (injected at deploy time) takes precedence.
// Empty string = same-origin (proxied by dev server or nginx).
function resolveApiBaseUrl(): string {
  const runtimeUrl = (window as any).__IRIS_ENV__?.REACT_APP_API_URL;
  if (typeof runtimeUrl === 'string' && runtimeUrl.length > 0) return runtimeUrl;
  const buildUrl = process.env.REACT_APP_API_URL;
  if (typeof buildUrl === 'string' && buildUrl.length > 0) return buildUrl;
  return '';
}
const API_BASE_URL = resolveApiBaseUrl();
const API_V1_PREFIX = '/api/v1';

interface AuthState {
  csrfToken: string | null;
  user: any | null;
}

class ApiService {
  private client: AxiosInstance;
  private authState: AuthState = {
    csrfToken: null,
    user: null
  };

  constructor() {
    this.client = axios.create({
      baseURL: `${API_BASE_URL}${API_V1_PREFIX}`,
      headers: {
        'Content-Type': 'application/json',
      },
      withCredentials: true, // Important: Send cookies with requests
    });

    // Request interceptor to add CSRF token and block unauthenticated requests
    this.client.interceptors.request.use(
      (config) => {
        // Skip auth check for login and refresh endpoints
        const url = config.url || '';
        const isAuthEndpoint = url.includes('/auth/login') || url.includes('/auth/refresh');

        // Block requests when no session exists (prevents CORS errors on login page)
        if (!isAuthEndpoint && !sessionStorage.getItem('user')) {
          return Promise.reject(new axios.Cancel('No active session'));
        }

        // Add CSRF token for non-safe methods
        if (this.authState.csrfToken &&
            config.method &&
            !['get', 'head', 'options'].includes(config.method.toLowerCase())) {
          config.headers['X-CSRF-Token'] = this.authState.csrfToken;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor to handle token refresh and centralized error reporting
    this.client.interceptors.response.use(
      (response) => response,
      async (error: AxiosError) => {
        const originalRequest = error.config as any;

        // --- Network error (no response at all) — skip retry ---
        if (!error.response) {
          return Promise.reject(error);
        }

        // --- 401: attempt token refresh (but NOT for login/register endpoints) ---
        const requestUrl = originalRequest?.url || '';
        const isLoginOrRegister = requestUrl.includes('/auth/login') || requestUrl.includes('/auth/register');
        if (error.response?.status === 401 && !originalRequest._retry && !isLoginOrRegister) {
          originalRequest._retry = true;

          try {
            // Try to refresh the token
            const response = await this.client.post('/auth/refresh');

            // Update CSRF token if provided
            if (response.data.csrf_token) {
              this.authState.csrfToken = response.data.csrf_token;
            }

            // Retry original request with new CSRF token
            if (this.authState.csrfToken &&
                originalRequest.method &&
                !['get', 'head', 'options'].includes(originalRequest.method.toLowerCase())) {
              originalRequest.headers['X-CSRF-Token'] = this.authState.csrfToken;
            }

            return this.client(originalRequest);
          } catch (refreshError) {
            // Clear auth state and redirect to login
            this.authState = { csrfToken: null, user: null };
            sessionStorage.removeItem('user');
            apiErrorEmitter.emit({
              message: 'Your session has expired. Redirecting to login.',
              type: 'auth',
              severity: 'warning',
              status: 401,
            });
            window.location.href = '/login';
            return Promise.reject(refreshError);
          }
        }

        // --- Centralized error emission ---
        this.emitApiError(error);

        return Promise.reject(error);
      }
    );
  }

  // ---- Centralized Error Emission ----

  private emitApiError(error: AxiosError): void {
    // Network error (no response at all)
    if (!error.response) {
      apiErrorEmitter.emit({
        message: 'Network connection error. Please check your internet connection.',
        type: 'network',
        severity: 'warning',
      });
      return;
    }

    const status = error.response.status;
    const data = error.response.data as any;
    const serverMessage = data?.detail || data?.message || '';

    switch (status) {
      case 401:
        // Already handled above in the refresh flow; only emit if refresh was not attempted
        break;

      case 403:
        apiErrorEmitter.emit({
          message: 'You do not have permission to perform this action.',
          type: 'forbidden',
          severity: 'error',
          status,
          details: serverMessage,
        });
        break;

      case 422:
        apiErrorEmitter.emit({
          message: serverMessage || 'Validation error. Please check your input.',
          type: 'validation',
          severity: 'warning',
          status,
          details: serverMessage,
        });
        break;

      case 500:
      case 502:
      case 503:
      case 504:
        apiErrorEmitter.emit({
          message: 'An unexpected server error occurred. Please try again later.',
          type: 'server',
          severity: 'error',
          status,
          details: serverMessage,
        });
        break;

      default:
        // Only emit for 4xx/5xx that are not already covered
        if (status >= 400) {
          apiErrorEmitter.emit({
            message: serverMessage || `Request failed with status ${status}.`,
            type: 'unknown',
            severity: 'error',
            status,
            details: serverMessage,
          });
        }
        break;
    }
  }

  // Authentication
  async login(email: string, password: string) {
    const response = await this.client.post('/auth/login', { email, password });

    // Store CSRF token and user info
    this.authState.csrfToken = response.data.csrf_token;
    this.authState.user = response.data.user;

    // Store user info in sessionStorage for persistence (not sensitive tokens)
    if (response.data.user) {
      sessionStorage.setItem('user', JSON.stringify(response.data.user));
    }

    return response.data;
  }

  async register(email: string, password: string, full_name: string, role: string) {
    return this.client.post('/auth/register', { email, password, full_name, role });
  }

  async getCurrentUser() {
    const response = await this.client.get('/auth/me');
    this.authState.user = response.data;
    // Restore CSRF token on page refresh
    if (response.data.csrf_token) {
      this.authState.csrfToken = response.data.csrf_token;
    }
    return response;
  }

  async logout() {
    try {
      await this.client.post('/auth/logout');
    } catch (error) {
      // Logout error - proceed with local cleanup regardless
    }

    // Clear auth state
    this.authState = { csrfToken: null, user: null };
    sessionStorage.removeItem('user');

    // Redirect to login
    window.location.href = '/login';
  }

  // Getter for current user
  getUser() {
    if (!this.authState.user) {
      const storedUser = sessionStorage.getItem('user');
      if (storedUser) {
        this.authState.user = JSON.parse(storedUser);
      }
    }
    return this.authState.user;
  }

  // Check if user is authenticated
  isAuthenticated() {
    return !!this.authState.user || !!sessionStorage.getItem('user');
  }

  // Inventory
  async getInventoryStatus(params?: any) {
    return this.client.get('/inventory/status', { params });
  }

  async updateInventory(data: any) {
    return this.client.post('/inventory/update', data);
  }

  // Replenishment
  async calculateReplenishment(data: any) {
    return this.client.post('/replenishment/calculate', data);
  }

  async generateOrders(data: any) {
    return this.client.post('/replenishment/generate-orders', data);
  }

  async approveOrder(orderId: string, approvedQuantity: number) {
    return this.client.put(`/replenishment/approve-order/${orderId}`, {
      order_id: orderId,
      approved_quantity: approvedQuantity,
    });
  }

  async rejectOrder(orderId: string) {
    return this.client.put(`/replenishment/reject-order/${orderId}`);
  }

  async bulkApproveOrders(approvals: Array<{ order_id: string; approved_quantity: number }>) {
    return this.client.post('/replenishment/approve-bulk', approvals);
  }

  async getCriticalItems(thresholdDays?: number) {
    return this.client.get('/replenishment/critical-items', {
      params: { threshold_days: thresholdDays },
    });
  }

  async getOrderHistory(params?: { status?: string; date_from?: string; date_to?: string; store_id?: string; limit?: number; offset?: number; brands?: string; cities?: string; stores?: string; countries?: string; retailers?: string; product_id?: string }) {
    return this.client.get('/replenishment/history', { params });
  }

  async getTransferHistory(params?: { status?: string; date_from?: string; date_to?: string; limit?: number; offset?: number }) {
    return this.client.get('/logistics/transfers', { params });
  }

  async approveTransfer(transferId: string, approvedQty: number) {
    return this.client.put(`/logistics/transfers/${transferId}/approve`, { approved_qty: approvedQty });
  }

  async getMonthlyPurchaseProjections(params?: { brands?: string; categories?: string; cities?: string; stores?: string }) {
    const response = await this.client.get('/replenishment/monthly-purchase-projections', { params });
    return response.data;
  }

  async getOrderValidationLines(params?: { brands?: string; categories?: string; cities?: string; stores?: string }) {
    const response = await this.client.get('/replenishment/order-validation-lines', { params });
    return response.data;
  }

  // Forecasting
  async generateForecast(data: any) {
    return this.client.post('/forecast/generate', data);
  }

  async getForecastResults(productId?: string, storeId?: string, limit?: number) {
    return this.client.get('/forecast/results', {
      params: { product_id: productId, store_id: storeId, limit },
    });
  }

  async getForecastProjection(
    productId?: string,
    storeId?: string,
    months: number = 18,
    opts?: {
      brand?: string;
      category?: string;
      city?: string;
      product_family?: string;
      sku?: string;
      abc_classification?: string;
      start_month?: string;
      page?: number;
      page_size?: number;
      sort_by?: string;
      sort_dir?: string;
    }
  ) {
    const params: any = { months };
    if (opts?.start_month) params.start_month = opts.start_month;
    if (productId) params.product_id = productId;
    if (storeId) params.store_id = storeId;
    if (opts?.brand) params.brand = opts.brand;
    if (opts?.category) params.category = opts.category;
    if (opts?.city) params.city = opts.city;
    if (opts?.product_family) params.product_family = opts.product_family;
    if (opts?.sku) params.sku = opts.sku;
    if (opts?.abc_classification) params.abc_classification = opts.abc_classification;
    if (opts?.page) params.page = opts.page;
    if (opts?.page_size) params.page_size = opts.page_size;
    if (opts?.sort_by) params.sort_by = opts.sort_by;
    if (opts?.sort_dir) params.sort_dir = opts.sort_dir;
    return this.client.get('/forecast/projection', { params });
  }

  // Stores
  async getStores(params?: any) {
    return this.client.get('/stores/', { params });
  }

  // Products
  async getProducts(params?: any) {
    return this.client.get('/products/', { params });
  }

  async getProductAutocomplete(field: string, search?: string, brand?: string, category?: string) {
    const params: any = { field };
    if (search) params.search = search;
    if (brand) params.brand = brand;
    if (category) params.category = category;
    const res = await this.client.get('/products/autocomplete', { params });
    return res.data as string[];
  }

  async getReplenishmentSettings(params?: any) {
    return this.client.get('/products/replenishment-settings/list', { params });
  }

  async updateReplenishmentSettings(productId: string, settings: any) {
    return this.client.put(`/products/${productId}/replenishment-settings`, settings);
  }

  async bulkUpdateReplenishmentSettings(updates: any[]) {
    return this.client.put('/products/replenishment-settings/bulk-update', updates);
  }

  // Chat
  async sendChatMessage(message: string, context?: any) {
    return this.client.post('/chat/message', { message, context });
  }

  async getChatHistory(limit?: number) {
    return this.client.get('/chat/history', { params: { limit } });
  }

  // Dashboard
  async getDashboardMetrics() {
    return this.client.get('/dashboard/metrics');
  }

  // Data Import
  async importSalesData(file: File) {
    const formData = new FormData();
    formData.append('file', file);
    return this.client.post('/data/import/sales', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  }

  async importProducts(file: File) {
    const formData = new FormData();
    formData.append('file', file);
    return this.client.post('/data/import/products', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  }

  async importInventory(file: File) {
    const formData = new FormData();
    formData.append('file', file);
    return this.client.post('/data/import/inventory', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  }

  // Alerts & Notifications
  async getAlertSummary() {
    return this.client.get('/alerts/summary');
  }

  async getAlerts(params?: any) {
    return this.client.get('/alerts/', { params });
  }

  async getAlert(alertId: string) {
    return this.client.get(`/alerts/${alertId}`);
  }

  async acknowledgeAlerts(alertIds: string[]) {
    return this.client.post('/alerts/acknowledge', { alert_ids: alertIds });
  }

  async resolveAlerts(alertIds: string[], resolutionNotes?: string) {
    return this.client.post('/alerts/resolve', {
      alert_ids: alertIds,
      resolution_notes: resolutionNotes
    });
  }

  async evaluateAlerts() {
    return this.client.post('/alerts/evaluate');
  }

  // Notifications
  async getMyNotifications(params?: any) {
    return this.client.get('/alerts/notifications/me', { params });
  }

  async markNotificationsRead(notificationIds: string[]) {
    return this.client.post('/alerts/notifications/mark-read', notificationIds);
  }

  // Notification Preferences
  async getMyNotificationPreferences() {
    return this.client.get('/alerts/preferences/me');
  }

  async updateMyNotificationPreferences(preferences: any) {
    return this.client.put('/alerts/preferences/me', preferences);
  }

  // Alert Rules
  async getAlertRules(params?: any) {
    return this.client.get('/alerts/rules/', { params });
  }

  async createAlertRule(data: any) {
    return this.client.post('/alerts/rules/', data);
  }

  async updateAlertRule(ruleId: string, data: any) {
    return this.client.put(`/alerts/rules/${ruleId}`, data);
  }

  async deleteAlertRule(ruleId: string) {
    return this.client.delete(`/alerts/rules/${ruleId}`);
  }

  // ============================================================================
  // Parameter Management
  // ============================================================================

  // Parameters CRUD
  async getParameters(params?: any) {
    return this.client.get('/parameters/', { params });
  }

  async getParameter(paramId: string) {
    return this.client.get(`/parameters/${paramId}`);
  }

  async createParameter(data: any, changeReason?: string) {
    return this.client.post('/parameters/', data, {
      params: { change_reason: changeReason }
    });
  }

  async updateParameter(paramId: string, data: any, changeReason?: string) {
    return this.client.put(`/parameters/${paramId}`, data, {
      params: { change_reason: changeReason }
    });
  }

  async deleteParameter(paramId: string, changeReason?: string) {
    return this.client.delete(`/parameters/${paramId}`, {
      params: { change_reason: changeReason }
    });
  }

  // Bulk Operations
  async bulkUpdateParameters(data: any) {
    return this.client.post('/parameters/bulk-update', data);
  }

  // Templates
  async getParameterTemplates(params?: any) {
    return this.client.get('/parameters/templates/', { params });
  }

  async createParameterTemplate(data: any) {
    return this.client.post('/parameters/templates/', data);
  }

  async deleteParameterTemplate(templateId: string) {
    return this.client.delete(`/parameters/templates/${templateId}`);
  }

  async applyParameterTemplate(templateId: string, entityIds: string[]) {
    return this.client.post(`/parameters/templates/${templateId}/apply`, entityIds);
  }

  // History
  async getParameterHistory(params?: any) {
    return this.client.get('/parameters/history/', { params });
  }

  // Import/Export
  async importParameters(file: File, paramType?: string) {
    const formData = new FormData();
    formData.append('file', file);
    if (paramType) {
      formData.append('param_type', paramType);
    }
    return this.client.post('/parameters/import', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  }

  async exportParameters(params?: any) {
    return this.client.get('/parameters/export', {
      params,
      responseType: 'blob'
    });
  }

  // ============================================================================
  // REPORTS & ANALYTICS
  // ============================================================================

  // Daily Replenishment Plan
  async getDailyReplenishmentPlan(params?: {
    target_date?: string;
    store_id?: string;
  }) {
    return this.client.get('/reports/daily-replenishment-plan', { params });
  }

  // Stock Status Report
  async getStockStatusReport(params?: {
    store_id?: string;
    category?: string;
    brand?: string;
    cities?: string;
    abc_classifications?: string;
    start_date?: string;
    end_date?: string;
    status?: string;
  }) {
    return this.client.get('/reports/stock-status', { params });
  }

  // 12-month Stock Projection (forecasts + orders + transfers)
  async getStockProjection(params?: {
    store_id?: string;
    category?: string;
    brand?: string;
    months?: number;
  }) {
    return this.client.get('/reports/stock-projection', { params });
  }

  // Order Tracking Report
  async getOrderTrackingReport(params?: {
    start_date?: string;
    end_date?: string;
    status?: string;
    store_id?: string;
  }) {
    return this.client.get('/reports/order-tracking', { params });
  }

  // Stockout Analysis Report
  async getStockoutAnalysisReport(params?: {
    start_date?: string;
    end_date?: string;
    store_id?: string;
  }) {
    return this.client.get('/reports/stockout-analysis', { params });
  }

  // Excess Inventory Report
  async getExcessInventoryReport(params?: {
    threshold?: number;
    store_id?: string;
    category?: string;
  }) {
    return this.client.get('/reports/excess-inventory', { params });
  }

  // Expiring Products Report
  async getExpiringProductsReport(params?: {
    days_threshold?: number;
    store_id?: string;
  }) {
    return this.client.get('/reports/expiring-products', { params });
  }

  // Generic Report Generation
  async generateReport(data: any) {
    return this.client.post('/reports/generate', data);
  }

  // Export Reports
  async exportReportCSV(reportType: string, params?: any) {
    return this.client.get(`/reports/export/csv/${reportType}`, {
      params,
      responseType: 'blob'
    });
  }

  async exportReportJSON(reportType: string, params?: any) {
    return this.client.get(`/reports/export/json/${reportType}`, {
      params,
      responseType: 'blob'
    });
  }

  // ============================================================================
  // ANALYTICAL REPORTS
  // ============================================================================

  // Sales Trends Analysis
  async getSalesTrendsAnalysis(params?: {
    start_date?: string;
    end_date?: string;
    store_id?: string;
    category?: string;
  }) {
    return this.client.get('/reports/sales-trends', { params });
  }

  // Supplier Performance
  async getSupplierPerformance(params?: {
    start_date?: string;
    end_date?: string;
  }) {
    return this.client.get('/reports/supplier-performance', { params });
  }

  // Store Performance Comparison
  async getStorePerformance(params?: {
    start_date?: string;
    end_date?: string;
  }) {
    return this.client.get('/reports/store-performance', { params });
  }

  // Enhanced Store Performance Analysis
  async getStorePerformanceEnhanced(params?: {
    start_date?: string;
    end_date?: string;
    brands?: string;
    stores?: string;
    cities?: string;
    categories?: string;
    abc_classifications?: string;
  }) {
    return this.client.get('/reports/store-performance-enhanced', { params });
  }

  // Category Performance Analysis
  async getCategoryPerformance(params?: {
    start_date?: string;
    end_date?: string;
    store_id?: string;
    brands?: string;
    stores?: string;
    cities?: string;
    abc_classifications?: string;
  }) {
    return this.client.get('/reports/category-performance', { params });
  }

  // Inventory Turnover Report
  async getInventoryTurnover(params?: {
    start_date?: string;
    end_date?: string;
    store_id?: string;
    category?: string;
  }) {
    return this.client.get('/reports/inventory-turnover', { params });
  }

  // Service Level Report
  async getServiceLevel(params?: {
    start_date?: string;
    end_date?: string;
    store_id?: string;
  }) {
    return this.client.get('/reports/service-level', { params });
  }

  // ============================================================================
  // EXECUTIVE DASHBOARD
  // ============================================================================

  // Get comprehensive KPIs
  async getExecutiveKPIs(params?: {
    start_date?: string;
    end_date?: string;
  }) {
    return this.client.get('/executive-dashboard/kpis', { params });
  }

  // Get working capital metrics
  async getWorkingCapital() {
    return this.client.get('/executive-dashboard/working-capital');
  }

  // Get cost savings metrics
  async getCostSavings(params?: {
    start_date?: string;
    end_date?: string;
  }) {
    return this.client.get('/executive-dashboard/cost-savings', { params });
  }

  // Export executive summary
  async exportExecutiveSummary(format: string, params?: {
    start_date?: string;
    end_date?: string;
  }) {
    return this.client.get('/executive-dashboard/export/summary', {
      params: { ...params, format },
      responseType: 'blob'
    });
  }

  // ============================================================================
  // CUSTOM REPORT BUILDER
  // ============================================================================

  // Generate custom report
  async generateCustomReport(config: any) {
    return this.client.post('/executive-dashboard/custom-reports/generate', config);
  }

  // Export custom report
  async exportCustomReport(config: any, format: string = 'csv') {
    return this.client.post('/executive-dashboard/custom-reports/export', config, {
      params: { format },
      responseType: 'blob'
    });
  }

  // ============================================================================
  // SCHEDULED REPORTS
  // ============================================================================

  // Create scheduled report
  async createScheduledReport(data: any) {
    return this.client.post('/executive-dashboard/scheduled-reports', data);
  }

  // Get all scheduled reports
  async getScheduledReports() {
    return this.client.get('/executive-dashboard/scheduled-reports');
  }

  // Get specific scheduled report
  async getScheduledReport(scheduleId: string) {
    return this.client.get(`/executive-dashboard/scheduled-reports/${scheduleId}`);
  }

  // Update scheduled report
  async updateScheduledReport(scheduleId: string, data: any) {
    return this.client.put(`/executive-dashboard/scheduled-reports/${scheduleId}`, data);
  }

  // Delete scheduled report
  async deleteScheduledReport(scheduleId: string) {
    return this.client.delete(`/executive-dashboard/scheduled-reports/${scheduleId}`);
  }

  // Toggle scheduled report active/inactive
  async toggleScheduledReport(scheduleId: string) {
    return this.client.post(`/executive-dashboard/scheduled-reports/${scheduleId}/toggle`);
  }

  // ============================================================================
  // DATA INTEGRATION - IMPORT/EXPORT
  // ============================================================================

  // Import CSV file
  async importCSV(file: File, dataType: string, mappingTemplateId?: string, validateOnly: boolean = false) {
    const formData = new FormData();
    formData.append('file', file);

    const params = new URLSearchParams({ data_type: dataType });
    if (mappingTemplateId) params.append('mapping_template_id', mappingTemplateId);
    if (validateOnly) params.append('validate_only', 'true');

    return this.client.post(`/integration/import/csv?${params.toString()}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  }

  // Import JSON file
  async importJSON(file: File, dataType: string, validateOnly: boolean = false) {
    const formData = new FormData();
    formData.append('file', file);

    const params = new URLSearchParams({ data_type: dataType });
    if (validateOnly) params.append('validate_only', 'true');

    return this.client.post(`/integration/import/json?${params.toString()}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  }

  // Import XML file
  async importXML(file: File, dataType: string, rootElement: string = 'data', itemElement: string = 'item') {
    const formData = new FormData();
    formData.append('file', file);

    const params = new URLSearchParams({
      data_type: dataType,
      root_element: rootElement,
      item_element: itemElement
    });

    return this.client.post(`/integration/import/xml?${params.toString()}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  }

  // Export data
  async exportData(dataType: string, format: string = 'csv', filters?: Record<string, any>) {
    return this.client.post('/integration/export', {
      data_type: dataType,
      format,
      filters
    }, {
      responseType: 'blob'
    });
  }

  // Get import history
  async getImportHistory(limit: number = 50) {
    return this.client.get('/integration/import/history', {
      params: { limit }
    });
  }

  // Rollback import
  async rollbackImport(importId: string) {
    return this.client.post(`/integration/import/${importId}/rollback`);
  }

  // ============================================================================
  // MAPPING TEMPLATES
  // ============================================================================

  // Create mapping template
  async createMappingTemplate(data: any) {
    return this.client.post('/integration/mapping-templates', data);
  }

  // Get all mapping templates
  async getMappingTemplates() {
    return this.client.get('/integration/mapping-templates');
  }

  // Delete mapping template
  async deleteMappingTemplate(templateId: string) {
    return this.client.delete(`/integration/mapping-templates/${templateId}`);
  }

  // ============================================================================
  // WEBHOOKS
  // ============================================================================

  // Create webhook
  async createWebhook(data: any) {
    return this.client.post('/integration/webhooks', data);
  }

  // Get all webhooks
  async getWebhooks() {
    return this.client.get('/integration/webhooks');
  }

  // Delete webhook
  async deleteWebhook(webhookId: string) {
    return this.client.delete(`/integration/webhooks/${webhookId}`);
  }

  // Toggle webhook
  async toggleWebhook(webhookId: string) {
    return this.client.post(`/integration/webhooks/${webhookId}/toggle`);
  }

  // ============================================================================
  // SCHEDULED IMPORTS
  // ============================================================================

  // Create scheduled import
  async createScheduledImport(data: any) {
    return this.client.post('/integration/scheduled-imports', data);
  }

  // Get all scheduled imports
  async getScheduledImports() {
    return this.client.get('/integration/scheduled-imports');
  }

  // Delete scheduled import
  async deleteScheduledImport(scheduleId: string) {
    return this.client.delete(`/integration/scheduled-imports/${scheduleId}`);
  }

  // ============================================================================
  // INTEGRATION MONITORING
  // ============================================================================

  // Get integration logs
  async getIntegrationLogs(limit: number = 100, operation?: string) {
    return this.client.get('/integration/logs', {
      params: { limit, operation }
    });
  }

  // Get integration stats
  async getIntegrationStats() {
    return this.client.get('/integration/stats');
  }

  // ============================================================================
  // STOCK-OUT MANAGEMENT
  // ============================================================================

  // Detect stock-out periods
  async detectStockOutPeriods(params?: {
    start_date?: string;
    end_date?: string;
    store_id?: string;
    product_id?: string;
  }) {
    return this.client.get('/stockout/detect', { params });
  }

  // Get root cause analysis
  async getRootCauseAnalysis(params?: {
    start_date?: string;
    end_date?: string;
  }) {
    return this.client.get('/stockout/root-cause-analysis', { params });
  }

  // Generate recovery plan
  async generateRecoveryPlan(inventoryId: string) {
    return this.client.get(`/stockout/recovery-plan/${inventoryId}`);
  }

  // Get stock-out trends
  async getStockOutTrends(days: number = 90) {
    return this.client.get('/stockout/trends', {
      params: { days }
    });
  }

  // Get prevention recommendations
  async getPreventionRecommendations(storeId?: string) {
    return this.client.get('/stockout/prevention-recommendations', {
      params: { store_id: storeId }
    });
  }

  // Get dashboard summary
  async getStockOutDashboardSummary() {
    return this.client.get('/stockout/dashboard-summary');
  }

  // ============================================================================
  // WHAT-IF SCENARIO ENGINE
  // ============================================================================

  // Sensitivity Analysis
  async analyzeSensitivity(data: {
    parameter: string;
    base_value: number;
    variation_range?: number;
    steps?: number;
  }) {
    return this.client.post('/scenarios/sensitivity-analysis', data);
  }

  // Demand Scenario Modeling
  async modelDemandScenario(data: {
    scenario_name: string;
    demand_multiplier?: number;
    seasonal_factor?: number;
    growth_rate?: number;
    duration_days?: number;
  }) {
    return this.client.post('/scenarios/demand-scenario', data);
  }

  // Supply Chain Disruption Simulation
  async simulateDisruption(data: {
    scenario_name: string;
    disruption_type: string;
    severity?: number;
    duration_days?: number;
    affected_suppliers?: string[];
  }) {
    return this.client.post('/scenarios/disruption-scenario', data);
  }

  // Budget Variance
  async getBudgetVariance(fiscalYear?: number, brandId?: string) {
    const params: Record<string, any> = {};
    if (fiscalYear) params.fiscal_year = fiscalYear;
    if (brandId) params.brand_id = brandId;
    return this.client.get('/budgets/variance', { params });
  }

  // Budget Optimization
  async optimizeBudget(data: {
    total_budget: number;
    optimization_objective?: string;
    constraints?: Record<string, any>;
  }) {
    return this.client.post('/scenarios/budget-optimization', data);
  }

  // Service Level vs Cost Tradeoff
  async analyzeServiceLevelTradeoff(params?: {
    min_service_level?: number;
    max_service_level?: number;
    steps?: number;
  }) {
    return this.client.get('/scenarios/service-level-tradeoff', { params });
  }

  // Create Scenario
  async createScenario(data: {
    scenario_name: string;
    scenario_type: string;
    description?: string;
    parameters: Record<string, any>;
  }) {
    return this.client.post('/scenarios/saved', data);
  }

  // Get All Scenarios
  async getAllScenarios(scenarioType?: string) {
    return this.client.get('/scenarios/saved', {
      params: { scenario_type: scenarioType }
    });
  }

  // Get Scenario by ID
  async getScenario(scenarioId: string) {
    return this.client.get(`/scenarios/saved/${scenarioId}`);
  }

  // Delete Scenario
  async deleteScenario(scenarioId: string) {
    return this.client.delete(`/scenarios/saved/${scenarioId}`);
  }

  // Compare Scenarios
  async compareScenarios(data: {
    scenario_ids: string[];
    comparison_metrics?: string[];
  }) {
    return this.client.post('/scenarios/compare-scenarios', data);
  }

  // Get Quick Insights
  async getQuickInsights() {
    return this.client.get('/scenarios/quick-insights');
  }

  // ============================================================================
  // ADVANCED ANALYTICS
  // ============================================================================

  // ABC-XYZ Classification
  async getABCXYZClassification(periodDays: number = 90) {
    return this.client.get('/advanced-analytics/abc-xyz-classification', {
      params: { period_days: periodDays }
    });
  }

  // Slow-Moving Stock
  async getSlowMovingStock(thresholdDays: number = 90, turnoverThreshold: number = 0.5) {
    return this.client.get('/advanced-analytics/slow-moving-stock', {
      params: { threshold_days: thresholdDays, turnover_threshold: turnoverThreshold }
    });
  }

  // Product Lifecycle Analysis
  async getProductLifecycle(productId?: string, lookbackDays: number = 365) {
    return this.client.get('/advanced-analytics/product-lifecycle', {
      params: { product_id: productId, lookback_days: lookbackDays }
    });
  }

  // Seasonal Decomposition
  async getSeasonalDecomposition(productId: string, lookbackDays: number = 730) {
    return this.client.get('/advanced-analytics/seasonal-decomposition', {
      params: { product_id: productId, lookback_days: lookbackDays }
    });
  }

  // Correlation Analysis
  async getCorrelationAnalysis(periodDays: number = 90, minCorrelation: number = 0.5) {
    return this.client.get('/advanced-analytics/correlation-analysis', {
      params: { period_days: periodDays, min_correlation: minCorrelation }
    });
  }

  // Price Elasticity
  async getPriceElasticity(productId?: string, periodDays: number = 180) {
    return this.client.get('/advanced-analytics/price-elasticity', {
      params: { product_id: productId, period_days: periodDays }
    });
  }

  // Market Basket Analysis
  async getMarketBasketAnalysis(periodDays: number = 90, minSupport: number = 0.01, minConfidence: number = 0.3) {
    return this.client.get('/advanced-analytics/market-basket-analysis', {
      params: { period_days: periodDays, min_support: minSupport, min_confidence: minConfidence }
    });
  }

  // ========================================================================
  // MULTI-ECHELON OPTIMIZATION
  // ========================================================================

  // DC to Store Allocation
  async getDCToStoreAllocation(productId?: string, forecastDays: number = 14) {
    return this.client.get('/multi-echelon/dc-to-store-allocation', {
      params: { product_id: productId, forecast_days: forecastDays }
    });
  }

  // Store to Store Transfers
  async getStoreToStoreTransfers(productId?: string, maxDistanceKm: number = 100.0) {
    return this.client.get('/multi-echelon/store-to-store-transfers', {
      params: { product_id: productId, max_distance_km: maxDistanceKm }
    });
  }

  // Supplier to DC Planning
  async getSupplierToDCPlanning(productId?: string, planningHorizonDays: number = 30) {
    return this.client.get('/multi-echelon/supplier-to-dc-planning', {
      params: { product_id: productId, planning_horizon_days: planningHorizonDays }
    });
  }

  // Network Balancing
  async getNetworkBalancing(productId?: string) {
    return this.client.get('/multi-echelon/network-balancing', {
      params: { product_id: productId }
    });
  }

  // Get multi-echelon transfers list
  async getMultiEchelonTransfers(params?: Record<string, unknown>) {
    return this.client.get('/multi-echelon/store-to-store-transfers', { params });
  }

  // Get multi-echelon network nodes
  async getMultiEchelonNodes(params?: Record<string, unknown>) {
    return this.client.get('/multi-echelon/network-balancing', { params });
  }

  // ========================================================================
  // NEW PRODUCT INTRODUCTION (NPI)
  // ========================================================================

  // Comparable Products
  async getComparableProducts(
    productId: string,
    category?: string,
    priceRangePct: number = 20.0,
    minSalesHistoryDays: number = 90
  ) {
    return this.client.get(`/npi/comparable-products/${productId}`, {
      params: {
        category,
        price_range_pct: priceRangePct,
        min_sales_history_days: minSalesHistoryDays
      }
    });
  }

  // Ramp-Up Curve
  async generateRampUpCurve(data: {
    comparable_product_ids: string[];
    launch_duration_weeks?: number;
    curve_type?: string;
  }) {
    return this.client.post('/npi/rampup-curve', data);
  }

  // Initial Allocation
  async calculateInitialAllocation(
    productId: string,
    totalLaunchQuantity: number,
    allocationMethod: string = 'proportional'
  ) {
    return this.client.get(`/npi/initial-allocation/${productId}`, {
      params: {
        total_launch_quantity: totalLaunchQuantity,
        allocation_method: allocationMethod
      }
    });
  }

  // Launch Performance
  async trackLaunchPerformance(
    productId: string,
    launchDate: string,
    weeksSinceLaunch?: number
  ) {
    return this.client.get(`/npi/launch-performance/${productId}`, {
      params: {
        launch_date: launchDate,
        weeks_since_launch: weeksSinceLaunch
      }
    });
  }

  // Create Launch Plan
  async createLaunchPlan(data: {
    product_id: string;
    launch_date: string;
    total_launch_quantity: number;
    comparable_product_ids: string[];
    allocation_method?: string;
    rampup_weeks?: number;
  }) {
    return this.client.post('/npi/launch-plan', data);
  }

  // ========================================================================
  // EXTERNAL SYSTEM INTEGRATIONS
  // ========================================================================

  // Integration Status
  async getIntegrationStatus(integrationType?: string) {
    return this.client.get('/integrations/status', {
      params: { integration_type: integrationType }
    });
  }

  // ERP
  async configureERP(data: any) {
    return this.client.post('/integrations/erp/configure', data);
  }

  async syncERPMasterData(integrationId: string, dataTypes: string[]) {
    return this.client.post('/integrations/erp/sync-master-data', {
      integration_id: integrationId,
      data_types: dataTypes
    });
  }

  // POS
  async configurePOS(data: any) {
    return this.client.post('/integrations/pos/configure', data);
  }

  // TMS
  async configureTMS(data: any) {
    return this.client.post('/integrations/tms/configure', data);
  }

  async scheduleDelivery(data: any) {
    return this.client.post('/integrations/tms/schedule-delivery', data);
  }

  async trackShipment(deliveryId: string, integrationId: string) {
    return this.client.get(`/integrations/tms/track/${deliveryId}`, {
      params: { integration_id: integrationId }
    });
  }

  // Supplier
  async configureSupplier(data: any) {
    return this.client.post('/integrations/supplier/configure', data);
  }

  async sendPurchaseOrders(integrationId: string, purchaseOrders: any[]) {
    return this.client.post('/integrations/supplier/send-po', {
      integration_id: integrationId,
      purchase_orders: purchaseOrders
    });
  }

  // ==================== Advanced Forecasting ====================

  // Moving Average Forecast
  async getMovingAverageForecast(
    productId?: string,
    forecastDays: number = 30,
    windowDays: number = 7,
    locationId?: string
  ) {
    return this.client.get('/advanced-forecasting/moving-average', {
      params: {
        product_id: productId,
        forecast_days: forecastDays,
        window_days: windowDays,
        location_id: locationId
      }
    });
  }

  // Exponential Smoothing Forecast
  async getExponentialSmoothingForecast(
    productId?: string,
    forecastDays: number = 30,
    alpha: number = 0.3,
    locationId?: string
  ) {
    return this.client.get('/advanced-forecasting/exponential-smoothing', {
      params: {
        product_id: productId,
        forecast_days: forecastDays,
        alpha: alpha,
        location_id: locationId
      }
    });
  }

  // ARIMA-like Forecast
  async getARIMALikeForecast(
    productId?: string,
    forecastDays: number = 30,
    locationId?: string
  ) {
    return this.client.get('/advanced-forecasting/arima-like', {
      params: {
        product_id: productId,
        forecast_days: forecastDays,
        location_id: locationId
      }
    });
  }

  // Ensemble Forecast
  async getEnsembleForecast(data: {
    product_id?: string;
    forecast_days?: number;
    methods?: string[];
    weights?: Record<string, number>;
    location_id?: string;
  }) {
    return this.client.post('/advanced-forecasting/ensemble', data);
  }

  // Weather-Adjusted Forecast
  async getWeatherAdjustedForecast(data: {
    product_id: string;
    location_id: string;
    forecast_days?: number;
    weather_data?: Array<{
      date: string;
      temperature: number;
      precipitation?: number;
      condition: string;
    }>;
  }) {
    return this.client.post('/advanced-forecasting/weather-adjusted', data);
  }

  // Competitive-Adjusted Forecast
  async getCompetitiveAdjustedForecast(data: {
    product_id: string;
    forecast_days?: number;
    competitive_events?: Array<{
      date: string;
      event_type: string;
      impact_factor: number;
      description?: string;
    }>;
    location_id?: string;
  }) {
    return this.client.post('/advanced-forecasting/competitive-adjusted', data);
  }

  // Apply Manual Overrides
  async applyManualOverrides(data: {
    base_forecast: any;
    overrides: Array<{
      date: string;
      quantity: number;
      reason: string;
      user: string;
    }>;
  }) {
    return this.client.post('/advanced-forecasting/apply-overrides', data);
  }

  // Cannibalization Analysis
  async analyzeCannibalization(data: {
    new_product_id: string;
    comparable_product_ids: string[];
    launch_date: string;
    analysis_weeks?: number;
  }) {
    return this.client.post('/advanced-forecasting/cannibalization', data);
  }

  // Forecast Accuracy
  async getForecastAccuracy(
    productId: string,
    periodStart: string,
    periodEnd: string,
    locationId?: string
  ) {
    return this.client.get('/advanced-forecasting/accuracy', {
      params: {
        product_id: productId,
        period_start: periodStart,
        period_end: periodEnd,
        location_id: locationId
      }
    });
  }

  // Forecast Accuracy Dashboard
  async getForecastAccuracyDashboard(
    periodStart: string,
    periodEnd: string,
    locationId?: string,
    topN: number = 20,
    brand?: string,
    city?: string
  ) {
    return this.client.get('/advanced-forecasting/accuracy-dashboard', {
      params: {
        period_start: periodStart,
        period_end: periodEnd,
        location_id: locationId,
        top_n: topN,
        brand,
        city
      }
    });
  }

  // Forecast Accuracy Monthly Breakdown
  async getForecastAccuracyMonthly(
    periodStart: string,
    periodEnd: string,
    locationId?: string,
    brand?: string,
    city?: string
  ) {
    return this.client.get('/advanced-forecasting/accuracy-monthly', {
      params: {
        period_start: periodStart,
        period_end: periodEnd,
        location_id: locationId,
        brand,
        city
      }
    });
  }

  // ==================== Seasonal Adjustment ====================

  // Event Calendar
  async getEventCalendar(year: number, eventTypes?: string) {
    return this.client.get(`/seasonal-adjustment/event-calendar/${year}`, {
      params: {
        event_types: eventTypes
      }
    });
  }

  // Event Adjustment
  async applyEventAdjustments(data: {
    product_id?: string;
    start_date: string;
    end_date: string;
    events?: any[];
  }) {
    return this.client.post('/seasonal-adjustment/event-adjustment', data);
  }

  // Weather Adjustment
  async applyWeatherAdjustments(data: {
    product_id: string;
    start_date: string;
    end_date: string;
    weather_data: Array<{
      date: string;
      temperature: number;
      condition: string;
    }>;
  }) {
    return this.client.post('/seasonal-adjustment/weather-adjustment', data);
  }

  // Year-over-Year Comparison
  async getYearOverYearComparison(
    year1: number,
    year2: number,
    productId?: string,
    locationId?: string,
    opts?: {
      brand?: string;
      category?: string;
      city?: string;
      stores?: string[];
      dateFrom?: string;
      dateTo?: string;
    }
  ) {
    const params: Record<string, any> = {
      year1,
      year2,
      product_id: productId,
      location_id: locationId,
    };
    if (opts?.brand) params.brand = opts.brand;
    if (opts?.category) params.category = opts.category;
    if (opts?.city) params.city = opts.city;
    if (opts?.stores?.length) params.stores = opts.stores;
    if (opts?.dateFrom) params.date_from = opts.dateFrom;
    if (opts?.dateTo) params.date_to = opts.dateTo;
    return this.client.get('/seasonal-adjustment/year-over-year', { params });
  }

  // Seasonal Profiles
  async getSeasonalProfiles(category?: string, brand?: string) {
    return this.client.get('/seasonal-adjustment/seasonal-profiles', {
      params: {
        category: category,
        brand: brand,
      }
    });
  }

  async createSeasonalProfile(data: {
    profile_name: string;
    product_category: string;
    monthly_factors: Record<number, number>;
    description?: string;
  }) {
    return this.client.post('/seasonal-adjustment/seasonal-profiles', data);
  }

  async applySeasonalProfile(data: {
    product_id: string;
    profile_id: string;
    start_date: string;
    end_date: string;
  }) {
    return this.client.post('/seasonal-adjustment/apply-seasonal-profile', data);
  }

  // ==================== User Management ====================

  // MFA
  async enableMFA(userId: string) {
    return this.client.post(`/user-management/mfa/enable/${userId}`);
  }

  async verifyMFA(userId: string, token: string, secret: string) {
    return this.client.post('/user-management/mfa/verify', { user_id: userId, token, secret });
  }

  async disableMFA(userId: string) {
    return this.client.post(`/user-management/mfa/disable/${userId}`);
  }

  async getMFABackupCodes(userId: string) {
    return this.client.get(`/user-management/mfa/backup-codes/${userId}`);
  }

  // OAuth
  async getOAuthProviders() {
    return this.client.get('/user-management/oauth/providers');
  }

  // API Keys
  async createAPIKey(userId: string, keyName: string, expiresInDays?: number, scopes?: string[]) {
    return this.client.post('/user-management/api-keys', {
      user_id: userId,
      key_name: keyName,
      expires_in_days: expiresInDays,
      scopes
    });
  }

  async listAPIKeys(userId: string) {
    return this.client.get(`/user-management/api-keys/${userId}`);
  }

  async revokeAPIKey(keyId: string) {
    return this.client.delete(`/user-management/api-keys/${keyId}`);
  }

  // Account Lockout
  async getLockoutStatus(email: string) {
    return this.client.get(`/user-management/lockout/status/${email}`);
  }

  async unlockAccount(email: string) {
    return this.client.post(`/user-management/lockout/unlock/${email}`);
  }

  async getLoginHistory(userId: string, limit: number = 50) {
    return this.client.get(`/user-management/login-history/${userId}`, { params: { limit } });
  }

  // Password Reset
  async requestPasswordReset(email: string) {
    return this.client.post('/user-management/password-reset/request', { email });
  }

  async verifyResetToken(token: string) {
    return this.client.post('/user-management/password-reset/verify', { token });
  }

  async resetPassword(token: string, newPassword: string) {
    return this.client.post('/user-management/password-reset/reset', {
      token,
      new_password: newPassword
    });
  }

  // Roles and Permissions
  async getRoles() {
    return this.client.get('/user-management/roles');
  }

  async getPermissions() {
    return this.client.get('/user-management/permissions');
  }

  async assignRole(userId: string, roleId: string) {
    return this.client.post('/user-management/roles/assign', { user_id: userId, role_id: roleId });
  }

  async getUserPermissions(userId: string) {
    return this.client.get(`/user-management/users/${userId}/permissions`);
  }

  async createRole(roleName: string, description: string, permissions: string[]) {
    return this.client.post('/user-management/roles/create', {
      role_name: roleName,
      description,
      permissions
    });
  }

  // ==================== AUDIT & COMPLIANCE ====================

  async getAuditLogs(params: {
    user_id?: string;
    action?: string;
    resource_type?: string;
    resource_id?: string;
    start_date?: string;
    end_date?: string;
    status?: string;
    limit?: number;
    offset?: number;
  }) {
    return this.client.get('/audit/logs', { params });
  }

  async createAuditLog(data: {
    user_id: string;
    action: string;
    resource_type: string;
    resource_id: string;
    before_value?: Record<string, any>;
    after_value?: Record<string, any>;
    ip_address: string;
    user_agent: string;
    status: string;
    error_message?: string;
  }) {
    return this.client.post('/audit/logs', data);
  }

  async getUserActivity(userId: string, startDate?: string, endDate?: string) {
    return this.client.get(`/audit/user-activity/${userId}`, {
      params: { start_date: startDate, end_date: endDate }
    });
  }

  async getActivityTimeline(params: {
    start_date?: string;
    end_date?: string;
    resource_type?: string;
    action?: string;
  }) {
    return this.client.get('/audit/timeline', { params });
  }

  async getResourceAccessHistory(resourceType: string, resourceId: string, startDate?: string, endDate?: string) {
    return this.client.get(`/audit/resource/${resourceType}/${resourceId}`, {
      params: { start_date: startDate, end_date: endDate }
    });
  }

  async generateComplianceReport(reportType: string, startDate?: string, endDate?: string) {
    return this.client.get('/audit/compliance-report', {
      params: { report_type: reportType, start_date: startDate, end_date: endDate }
    });
  }

  async exportAuditData(params: {
    start_date?: string;
    end_date?: string;
    format: string;
    user_id?: string;
    resource_type?: string;
  }) {
    return this.client.get('/audit/export', { params });
  }

  async getRetentionPolicyStatus() {
    return this.client.get('/audit/retention-policy');
  }

  async cleanupOldAuditLogs(daysToKeep: number) {
    return this.client.delete('/audit/cleanup', {
      params: { days_to_keep: daysToKeep }
    });
  }

  // ==================== PLANNER WORKSPACE ====================

  async runMorningRoutine(plannerId: string) {
    return this.client.post('/planner/morning-routine', null, {
      params: { planner_id: plannerId }
    });
  }

  async getStoreGrid(params: {
    page?: number;
    limit?: number;
    country?: string;
    city?: string;
    retailer?: string;
    point_of_sale?: string;
    region?: string;
    store_type?: string;
    status?: string;
    stockout_min?: number;
    service_level_min?: number;
    sort_field1?: string;
    sort_dir1?: string;
    sort_field2?: string;
    sort_dir2?: string;
  }) {
    return this.client.get('/planner/store-grid', { params });
  }

  async saveGridView(plannerId: string, viewName: string, config: any) {
    return this.client.post('/planner/store-grid/views', {
      planner_id: plannerId,
      view_name: viewName,
      config
    });
  }

  async getSavedViews(plannerId: string) {
    return this.client.get('/planner/store-grid/views', {
      params: { planner_id: plannerId }
    });
  }

  async previewBulkOperation(request: {
    operation_type: string;
    store_ids: string[];
    parameters: Record<string, any>;
  }) {
    return this.client.post('/planner/bulk-operations/preview', request);
  }

  async executeBulkOperation(request: {
    operation_type: string;
    store_ids: string[];
    parameters: Record<string, any>;
  }, plannerId: string) {
    return this.client.post('/planner/bulk-operations/execute', request, {
      params: { planner_id: plannerId }
    });
  }

  async undoBulkOperation(operationId: string) {
    return this.client.post(`/planner/bulk-operations/${operationId}/undo`);
  }

  async getDailyDashboard(plannerId: string) {
    return this.client.get('/planner/daily-dashboard', {
      params: { planner_id: plannerId }
    });
  }

  async saveWorkspace(plannerId: string, workspaceName: string, config: any) {
    return this.client.post('/planner/workspaces', {
      planner_id: plannerId,
      workspace_name: workspaceName,
      workspace_config: config
    });
  }

  async getWorkspaces(plannerId: string) {
    return this.client.get('/planner/workspaces', {
      params: { planner_id: plannerId }
    });
  }

  async recordMacro(plannerId: string, macroName: string, actions: any[]) {
    return this.client.post('/planner/macros', {
      planner_id: plannerId,
      macro_name: macroName,
      actions
    });
  }

  async getMacros(plannerId: string) {
    return this.client.get('/planner/macros', {
      params: { planner_id: plannerId }
    });
  }

  async executeMacro(macroId: string, context: any) {
    return this.client.post(`/planner/macros/${macroId}/execute`, { context });
  }

  async getSmartSuggestions(plannerId: string, contextType?: string) {
    return this.client.get('/planner/suggestions', {
      params: { planner_id: plannerId, context_type: contextType }
    });
  }

  async applySuggestion(suggestionId: string, plannerId: string) {
    return this.client.post(`/planner/suggestions/${suggestionId}/apply`, null, {
      params: { planner_id: plannerId }
    });
  }

  async getKeyboardShortcuts() {
    return this.client.get('/planner/keyboard-shortcuts');
  }

  // ==================== Delivery Schedule Management ====================

  // 2.1 Weekly Delivery Matrix
  async getWeeklyDeliveryMatrix(storeId: string) {
    return this.client.get(`/delivery/weekly-matrix/${storeId}`);
  }

  async updateWeeklyDeliveryMatrix(
    storeId: string,
    day: string,
    timeWindow: string,
    isAvailable: boolean
  ) {
    return this.client.put(`/delivery/weekly-matrix/${storeId}`, null, {
      params: { day, time_window: timeWindow, is_available: isAvailable }
    });
  }

  async getSpecialSchedules(storeId: string) {
    return this.client.get(`/delivery/special-schedules/${storeId}`);
  }

  // 2.2 Physical Delivery Constraints
  async getStoreConstraints(storeId: string) {
    return this.client.get(`/delivery/constraints/${storeId}`);
  }

  async updateStoreConstraints(storeId: string, constraints: any) {
    return this.client.put(`/delivery/constraints/${storeId}`, { constraints });
  }

  // 2.3 Geographic Clustering
  async getDeliveryClusters() {
    return this.client.get('/delivery/clusters');
  }

  async getClusterDetails(clusterId: string) {
    return this.client.get(`/delivery/clusters/${clusterId}`);
  }

  async createCluster(clusterName: string, storeIds: string[], region: string) {
    return this.client.post('/delivery/clusters', {
      cluster_name: clusterName,
      store_ids: storeIds,
      region
    });
  }

  // 2.4 Delivery Window Optimization
  async getAvailableWindows(date: string, clusterId?: string) {
    return this.client.get('/delivery/windows/available', {
      params: { date, cluster_id: clusterId }
    });
  }

  async bookDeliveryWindow(windowId: string, storeIds: string[], priority: string = 'standard') {
    return this.client.post('/delivery/windows/book', {
      window_id: windowId,
      store_ids: storeIds,
      priority
    });
  }

  async optimizeDeliverySchedule(dateRangeStart: string, dateRangeEnd: string) {
    return this.client.post('/delivery/optimize', null, {
      params: { date_range_start: dateRangeStart, date_range_end: dateRangeEnd }
    });
  }

  // 2.5 Schedule Exception Management
  async createEmergencyDelivery(storeId: string, urgency: string, items: any[]) {
    return this.client.post('/delivery/emergency', {
      store_id: storeId,
      urgency,
      items
    });
  }

  async getScheduleExceptions(startDate: string, endDate: string) {
    return this.client.get('/delivery/exceptions', {
      params: { start_date: startDate, end_date: endDate }
    });
  }

  async handleMissedDelivery(deliveryId: string, reason: string) {
    return this.client.post(`/delivery/missed-delivery/${deliveryId}`, null, {
      params: { reason }
    });
  }

  // ==================== Advanced Stockout Tracking ====================
  async detectStockouts(storeId?: string, productId?: string, startDate?: string, endDate?: string) {
    return this.client.get('/stockout-tracking/detect', {
      params: { store_id: storeId, product_id: productId, start_date: startDate, end_date: endDate }
    });
  }

  async getStockoutDetails(stockoutId: string) {
    return this.client.get(`/stockout-tracking/details/${stockoutId}`);
  }

  async markStockout(request: any) {
    return this.client.post('/stockout-tracking/mark', request);
  }

  async getReasonCodes() {
    return this.client.get('/stockout-tracking/reason-codes');
  }

  async estimateLostSales(stockoutId: string) {
    return this.client.get(`/stockout-tracking/estimate-lost-sales/${stockoutId}`);
  }

  async analyzeStockoutImpact(stockoutId: string) {
    return this.client.get(`/stockout-tracking/impact/${stockoutId}`);
  }

  async getStockoutTrackingAnalytics(startDate: string, endDate: string) {
    return this.client.get('/stockout-tracking/analytics', {
      params: { start_date: startDate, end_date: endDate }
    });
  }

  async createStockoutRecoveryPlan(stockoutId: string) {
    return this.client.post(`/stockout-tracking/recovery-plan/${stockoutId}`);
  }

  async getStockoutRecoveryStatus(planId: string) {
    return this.client.get(`/stockout-tracking/recovery-plan/${planId}/status`);
  }

  // Priority Rules Engine Methods
  async getPriorityHierarchy() {
    return this.client.get('/priority/hierarchy');
  }

  async getStorePriorities(region?: string) {
    return this.client.get('/priority/stores', { params: { region } });
  }

  async updateStorePriority(storeId: string, newPriority: number, reason: string, approvedBy: string) {
    return this.client.put(`/priority/stores/${storeId}/priority`, { new_priority: newPriority, reason, approved_by: approvedBy });
  }

  async bulkUpdatePriorities(updates: any[], approvedBy: string) {
    return this.client.post('/priority/stores/bulk-update', { updates, approved_by: approvedBy });
  }

  async getDynamicAdjustments(storeId?: string) {
    return this.client.get('/priority/adjustments', { params: { store_id: storeId } });
  }

  async createDynamicAdjustment(data: any) {
    return this.client.post('/priority/adjustments', data);
  }

  async getPerformanceAdjustments() {
    return this.client.get('/priority/adjustments/performance');
  }

  async calculateAllocation(productId: string, totalQuantity: number, strategy: string = 'priority_based') {
    return this.client.post(`/priority/allocate/${productId}`, null, { params: { total_quantity: totalQuantity, strategy } });
  }

  async optimizeAllocation(productId: string, totalQuantity: number, goals: string[]) {
    return this.client.post(`/priority/allocate/${productId}/optimize`, null, { params: { total_quantity: totalQuantity, goals } });
  }

  async getAllocationConstraints() {
    return this.client.get('/priority/allocation/constraints');
  }

  async getOverrides(status?: string) {
    return this.client.get('/priority/overrides', { params: { status } });
  }

  async createOverride(data: any) {
    return this.client.post('/priority/overrides', data);
  }

  async approveOverride(overrideId: string, approvedBy: string, notes?: string) {
    return this.client.post(`/priority/overrides/${overrideId}/approve`, { approved_by: approvedBy, notes });
  }

  async getBlacklistWhitelist() {
    return this.client.get('/priority/rules/blacklist-whitelist');
  }

  async getAllocationVisualization(productId: string) {
    return this.client.get(`/priority/visualization/${productId}`);
  }

  async simulateWhatIf(productId: string, scenario: any) {
    return this.client.post(`/priority/simulation/what-if/${productId}`, scenario);
  }

  async getAllocationAuditTrail(productId?: string, storeId?: string, startDate?: string) {
    return this.client.get('/priority/audit-trail', { params: { product_id: productId, store_id: storeId, start_date: startDate } });
  }

  // Order Consolidation Methods
  async getConsolidationOpportunities(level: string = 'all', minSavings: number = 100) {
    return this.client.get('/consolidation/opportunities', { params: { level, min_savings: minSavings } });
  }

  async getConsolidationHierarchy() {
    return this.client.get('/consolidation/hierarchy');
  }

  async getSupplierConstraints(supplierId?: string) {
    return this.client.get('/consolidation/suppliers', { params: { supplier_id: supplierId } });
  }

  async optimizeTransportation(orders: any[], goal: string = 'cost') {
    return this.client.post('/consolidation/transport/optimize', orders, { params: { optimization_goal: goal } });
  }

  async getConsolidationDashboard() {
    return this.client.get('/consolidation/dashboard');
  }

  // Competitive Intelligence API methods
  async getCompetitorLaunches(startDate?: string, endDate?: string, competitorId?: string) {
    return this.client.get('/competitive-intelligence/launches', { params: { start_date: startDate, end_date: endDate, competitor_id: competitorId } });
  }

  async getLaunchTimeline(launchId: string) {
    return this.client.get(`/competitive-intelligence/launches/${launchId}/timeline`);
  }

  async getCannibalizationMatrix(productId?: string, timeHorizonDays: number = 90) {
    return this.client.get('/competitive-intelligence/cannibalization/matrix', { params: { product_id: productId, time_horizon_days: timeHorizonDays } });
  }

  async getImpactCurves(productId: string, competitorLaunchId: string) {
    return this.client.get('/competitive-intelligence/cannibalization/impact-curves', { params: { product_id: productId, competitor_launch_id: competitorLaunchId } });
  }

  async predictLaunchImpact(launchId: string, ourProductIds?: string[]) {
    return this.client.post('/competitive-intelligence/prediction/launch-impact', ourProductIds, { params: { launch_id: launchId } });
  }

  async getSentimentAnalysis(launchId: string) {
    return this.client.get(`/competitive-intelligence/prediction/sentiment/${launchId}`);
  }

  async generateResponseScenarios(launchId: string, ourProductId: string) {
    return this.client.post('/competitive-intelligence/response/scenarios', null, { params: { launch_id: launchId, our_product_id: ourProductId } });
  }

  async getPromotionalResponses(launchId: string) {
    return this.client.get(`/competitive-intelligence/response/promotions/${launchId}`);
  }

  async recordActualImpact(launchId: string, productId: string, actualData: any) {
    return this.client.post('/competitive-intelligence/learning/record-impact', actualData, { params: { launch_id: launchId, product_id: productId } });
  }

  async getPredictionAccuracy(launchId: string) {
    return this.client.get(`/competitive-intelligence/learning/accuracy/${launchId}`);
  }

  async getLearningInsights(timePeriodDays: number = 90) {
    return this.client.get('/competitive-intelligence/learning/insights', { params: { time_period_days: timePeriodDays } });
  }

  async retrainModel(trainingDataIds: string[]) {
    return this.client.post('/competitive-intelligence/learning/retrain', trainingDataIds);
  }

  // Enhanced Parameter Management API methods
  async getParameterSuggestions(context?: string) {
    return this.client.get('/enhanced-parameters/suggestions', { params: { context } });
  }

  async getSeasonalReminders() {
    return this.client.get('/enhanced-parameters/reminders');
  }

  async getEnhancedParameterTemplates() {
    return this.client.get('/enhanced-parameters/templates');
  }

  async createEnhancedParameterTemplate(templateName: string, parameters: any, description: string, categories: string[]) {
    return this.client.post('/enhanced-parameters/templates', { template_name: templateName, parameters, description, categories });
  }

  async calculateParameterImpact(parameterChanges: any, currentInventory?: any) {
    return this.client.post('/enhanced-parameters/impact/calculate', { parameter_changes: parameterChanges, current_inventory: currentInventory });
  }

  async createChangeRequest(parameterChanges: any, justification: string, requestedBy: string) {
    return this.client.post('/enhanced-parameters/governance/change-request', { parameter_changes: parameterChanges, justification, requested_by: requestedBy });
  }

  async getApprovalWorkflow(requestId: string) {
    return this.client.get(`/enhanced-parameters/governance/workflow/${requestId}`);
  }

  async setupABTest(testName: string, variantA: any, variantB: any, durationDays: number, targetProducts: string[]) {
    return this.client.post('/enhanced-parameters/governance/ab-test', { test_name: testName, variant_a: variantA, variant_b: variantB, test_duration_days: durationDays, target_products: targetProducts });
  }

  async rollbackParameters(rollbackToVersion: string, reason: string) {
    return this.client.post('/enhanced-parameters/governance/rollback', { rollback_to_version: rollbackToVersion, reason });
  }

  async getParameterVersionHistory(parameterName: string) {
    return this.client.get(`/enhanced-parameters/governance/version-history/${parameterName}`);
  }

  // TMS Integration API methods
  async getTMSIntegrationStatus() {
    return this.client.get('/tms-integration/status');
  }

  async getSyncHistory(systemId?: string, startDate?: string, endDate?: string) {
    return this.client.get('/tms-integration/sync-history', { params: { system_id: systemId, start_date: startDate, end_date: endDate } });
  }

  async detectDiscrepancies(comparisonScope: string = 'all', timeWindowHours: number = 24) {
    return this.client.post('/tms-integration/discrepancies/detect', null, { params: { comparison_scope: comparisonScope, time_window_hours: timeWindowHours } });
  }

  async getDataSourcePriority(dataType: string) {
    return this.client.get(`/tms-integration/data-source-priority/${dataType}`);
  }

  async executeFallback(dataType: string, primarySourceFailed: boolean = true) {
    return this.client.post('/tms-integration/fallback/execute', null, { params: { data_type: dataType, primary_source_failed: primarySourceFailed } });
  }

  async createReconciliationSession(discrepancyIds: string[], reconcilerId: string) {
    return this.client.post('/tms-integration/reconciliation/session', null, { params: { discrepancy_ids: discrepancyIds, reconciler_id: reconcilerId } });
  }

  async getSideBySideComparison(entityType: string, entityId: string) {
    return this.client.get(`/tms-integration/reconciliation/compare/${entityType}/${entityId}`);
  }

  async resolveDiscrepancy(discrepancyId: string, resolutionAction: string, resolutionValue?: any, notes?: string) {
    return this.client.post('/tms-integration/reconciliation/resolve', { discrepancy_id: discrepancyId, resolution_action: resolutionAction, resolution_value: resolutionValue, notes });
  }

  async runHealthCheck(systemId: string) {
    return this.client.post(`/tms-integration/health-check/${systemId}`);
  }

  async getValidationRules(entityType: string) {
    return this.client.get(`/tms-integration/validation-rules/${entityType}`);
  }

  async setupBackupMethod(systemId: string, backupType: string) {
    return this.client.post('/tms-integration/backup-method/setup', null, { params: { system_id: systemId, backup_type: backupType } });
  }

  async getPreventiveMetrics() {
    return this.client.get('/tms-integration/preventive/metrics');
  }

  // Partial Fulfillment API methods
  async evaluatePartialShipment(orderId: string, requestedQuantity: number, availableQuantity: number, productId: string) {
    return this.client.post('/partial-fulfillment/evaluate-partial-shipment', { order_id: orderId, requested_quantity: requestedQuantity, available_quantity: availableQuantity, product_id: productId });
  }

  async getAcceptanceCriteria() {
    return this.client.get('/partial-fulfillment/acceptance-criteria');
  }

  async allocateShortage(productId: string, availableQuantity: number, orders: any[], algorithm: string = 'priority_based') {
    return this.client.post('/partial-fulfillment/allocate-shortage', { product_id: productId, available_quantity: availableQuantity, orders, algorithm });
  }

  async compareAllocationAlgorithms(productId: string, availableQuantity: number, orders: any[]) {
    return this.client.post('/partial-fulfillment/compare-allocation-algorithms', { product_id: productId, available_quantity: availableQuantity, orders, algorithm: 'priority_based' });
  }

  async findSubstitutes(productId: string, requiredQuantity: number = 1, matchingCriteria?: string) {
    return this.client.get(`/partial-fulfillment/substitutes/${productId}`, { params: { required_quantity: requiredQuantity, matching_criteria: matchingCriteria } });
  }

  async getSubstitutionRules() {
    return this.client.get('/partial-fulfillment/substitution-rules');
  }

  async createNotification(notificationType: string, orderId: string, customerId: string, details: any) {
    return this.client.post('/partial-fulfillment/notifications', { notification_type: notificationType, order_id: orderId, customer_id: customerId, details });
  }

  async getCommunicationStatus(orderId?: string) {
    return this.client.get('/partial-fulfillment/communications/status', { params: { order_id: orderId } });
  }

  async getPartialFulfillmentMetrics(timePeriodDays: number = 30) {
    return this.client.get('/partial-fulfillment/performance/metrics', { params: { time_period_days: timePeriodDays } });
  }

  async getCustomerImpactAnalysis(customerId?: string) {
    return this.client.get('/partial-fulfillment/performance/customer-impact', { params: { customer_id: customerId } });
  }

  // Communication System API methods
  async getKanbanBoard(pipelineType: string = 'orders') {
    return this.client.get('/communication-system/kanban-board', { params: { pipeline_type: pipelineType } });
  }

  async getTimelineView(startDate: string, endDate: string) {
    return this.client.get('/communication-system/timeline-view', { params: { start_date: startDate, end_date: endDate } });
  }

  async getMapView() {
    return this.client.get('/communication-system/map-view');
  }

  async getCommunicationTemplates(category?: string) {
    return this.client.get('/communication-system/templates', { params: { category } });
  }

  async sendCommunication(templateId: string, recipientId: string, variables: any) {
    return this.client.post('/communication-system/send-communication', { template_id: templateId, recipient_id: recipientId, variables });
  }

  async getPortalAccess(portalType: string, userId: string) {
    return this.client.get(`/communication-system/portal/${portalType}/${userId}`);
  }

  async getRealTimeUpdates(entityType: string, entityId?: string) {
    return this.client.get('/communication-system/real-time-updates', { params: { entity_type: entityType, entity_id: entityId } });
  }

  async getExceptions(severity?: string, status?: string) {
    return this.client.get('/communication-system/exceptions', { params: { severity, status } });
  }

  async getEscalationWorkflow() {
    return this.client.get('/communication-system/escalation-workflow');
  }

  async createException(title: string, severity: string, category: string, relatedEntity: any) {
    return this.client.post('/communication-system/exceptions', { title, severity, category, related_entity: relatedEntity });
  }

  // Industry Vertical Configuration API methods
  async getPharmacyConfig() {
    return this.client.get('/industry-verticals/pharmacy-config');
  }

  async getBeautyConfig() {
    return this.client.get('/industry-verticals/beauty-config');
  }

  async getLuxuryConfig() {
    return this.client.get('/industry-verticals/luxury-config');
  }

  async getFoodBeverageConfig() {
    return this.client.get('/industry-verticals/food-beverage-config');
  }

  async getConfigurationSwitching() {
    return this.client.get('/industry-verticals/configuration-switching');
  }

  async switchVerticalConfig(data: any) {
    return this.client.post('/industry-verticals/switch-config', data);
  }

  async validateVerticalConfig(data: any) {
    return this.client.post('/industry-verticals/validate-config', data);
  }

  async getActiveConfigurations(storeId?: string, departmentId?: string) {
    return this.client.get('/industry-verticals/active-configs', {
      params: { store_id: storeId, department_id: departmentId }
    });
  }

  async getVerticalStatistics() {
    return this.client.get('/industry-verticals/vertical-stats');
  }

  async bulkSwitchConfigurations(switches: any[]) {
    return this.client.post('/industry-verticals/bulk-switch', switches);
  }

  // Store Physical Attributes API methods
  async getStoreProfile(storeId: string) {
    return this.client.get('/store-physical-attributes/profile', { params: { store_id: storeId } });
  }

  async getAllStoreProfiles() {
    return this.client.get('/store-physical-attributes/profiles');
  }

  async getDisplayConfiguration(storeId: string) {
    return this.client.get('/store-physical-attributes/display-configuration', { params: { store_id: storeId } });
  }

  async getAllDisplayConfigurations() {
    return this.client.get('/store-physical-attributes/display-configurations');
  }

  async calculateStoreCapacity(storeId: string) {
    return this.client.get('/store-physical-attributes/capacity-calculation', { params: { store_id: storeId } });
  }

  async getEnvironmentalSpecifications(storeId: string) {
    return this.client.get('/store-physical-attributes/environmental-specifications', { params: { store_id: storeId } });
  }

  async getAllEnvironmentalSpecifications() {
    return this.client.get('/store-physical-attributes/environmental-specifications-all');
  }

  async getAccessibilityConstraints(storeId: string) {
    return this.client.get('/store-physical-attributes/accessibility-constraints', { params: { store_id: storeId } });
  }

  async getAllAccessibilityConstraints() {
    return this.client.get('/store-physical-attributes/accessibility-constraints-all');
  }

  async getComprehensiveStoreAttributes(storeId: string) {
    return this.client.get(`/store-physical-attributes/comprehensive/${storeId}`);
  }

  async getCapacitySummary() {
    return this.client.get('/store-physical-attributes/capacity-summary');
  }

  async getDeliveryWindowAvailability(storeId?: string, dayOfWeek?: string) {
    return this.client.get('/store-physical-attributes/delivery-window-availability', {
      params: { store_id: storeId, day_of_week: dayOfWeek }
    });
  }

  // Display Requirements API methods
  async getFacingConfigurations(productId?: string) {
    return this.client.get("/display-requirements/facing-configurations", {params: {product_id: productId}});
  }
  async getPlanograms(storeId?: string) {
    return this.client.get("/display-requirements/planograms", {params: {store_id: storeId}});
  }
  async calculateMinimumDisplay(productId: string, storeId: string, includeSafetyFactors: boolean = true) {
    return this.client.get("/display-requirements/minimum-display", {params: {product_id: productId, store_id: storeId, include_safety_factors: includeSafetyFactors}});
  }
  async optimizeStoreLayout(storeId: string) {
    return this.client.get("/display-requirements/layout-optimization", {params: {store_id: storeId}});
  }
  async getDisplayPerformanceAnalytics(storeId?: string, timePeriodDays: number = 30) {
    return this.client.get("/display-requirements/performance-analytics", {params: {store_id: storeId, time_period_days: timePeriodDays}});
  }

  // Financial Projection API methods
  async getFinancialWorkingCapital(asOfDate?: string) {
    return this.client.get("/financial-projection/working-capital", {params: {as_of_date: asOfDate}});
  }
  async getFinancialCashFlowForecast(forecastHorizonDays: number = 90, granularity: string = "weekly") {
    return this.client.get("/financial-projection/cash-flow-forecast", {params: {forecast_horizon_days: forecastHorizonDays, granularity}});
  }
  async getFinancialProfitabilityAnalysis(analysisType: string = "all", timePeriodDays: number = 90) {
    return this.client.get("/financial-projection/profitability-analysis", {params: {analysis_type: analysisType, time_period_days: timePeriodDays}});
  }
  async getFinancialBudgetStatus(budgetType: string = "all", fiscalYear?: number) {
    return this.client.get("/financial-projection/budget-status", {params: {budget_type: budgetType, fiscal_year: fiscalYear}});
  }
  async getFinancialROITracking(initiativeId?: string, includeProjections: boolean = true) {
    return this.client.get("/financial-projection/roi-tracking", {params: {initiative_id: initiativeId, include_projections: includeProjections}});
  }
  async getComprehensiveFinancialAnalysis(timePeriodDays: number = 90) {
    return this.client.get("/financial-projection/comprehensive-analysis", {params: {time_period_days: timePeriodDays}});
  }

  // ==================== Manual Override Hierarchy ====================

  // 15.1 Override Authority Matrix
  async getAuthorityMatrix(userRole?: string) {
    return this.client.get('/manual-override/authority-matrix', {
      params: { user_role: userRole }
    });
  }

  // 15.2 Data Source Priority Rules
  async getOverrideDataSourcePriority(entityType?: string) {
    return this.client.get('/manual-override/data-source-priority', {
      params: { entity_type: entityType }
    });
  }

  // 15.3 Override Justification System
  async getJustificationCategories() {
    return this.client.get('/manual-override/justification-categories');
  }

  // 15.4 Temporal Override Management
  async getTemporalOverrides(overrideType?: string, status?: string) {
    return this.client.get('/manual-override/temporal-overrides', {
      params: { override_type: overrideType, status }
    });
  }

  // 15.5 Override Audit and Analysis
  async getOverrideAuditAnalysis(startDate?: string, endDate?: string, entityType?: string) {
    return this.client.get('/manual-override/audit-analysis', {
      params: { start_date: startDate, end_date: endDate, entity_type: entityType }
    });
  }

  // Override Dashboard
  async getOverrideDashboard() {
    return this.client.get('/manual-override/dashboard');
  }

  // ==================== Seasonal Pattern Library ====================

  // 16.1 Pre-Built Industry Templates
  async getIndustryTemplates(industry?: string) {
    return this.client.get('/seasonal-patterns/industry-templates', {
      params: { industry }
    });
  }

  // 16.2 Regional Calendar Integration
  async getRegionalCalendars(region?: string, year: number = 2025, includeLocalEvents: boolean = true) {
    return this.client.get('/seasonal-patterns/regional-calendars', {
      params: { region, year, include_local_events: includeLocalEvents }
    });
  }

  // 16.3 Custom Pattern Creation
  async createCustomPattern(patternData: any) {
    return this.client.post('/seasonal-patterns/custom-patterns', patternData);
  }

  async getPatternBuilderTools() {
    return this.client.get('/seasonal-patterns/pattern-builder-tools');
  }

  // 16.4 Pattern Application Logic
  async applyPatternToProducts(data: {
    pattern_id: string;
    product_ids: string[];
    application_mode?: string;
    override_existing?: boolean;
  }) {
    return this.client.post('/seasonal-patterns/apply-pattern', data);
  }

  async getAutomaticAssignmentRules() {
    return this.client.get('/seasonal-patterns/assignment-rules');
  }

  // 16.5 Pattern Performance Tracking
  async getPatternPerformance(patternId?: string, timePeriodDays: number = 90) {
    return this.client.get('/seasonal-patterns/performance', {
      params: { pattern_id: patternId, time_period_days: timePeriodDays }
    });
  }

  async comparePatterns(patternIds: string[], comparisonMetric: string = 'accuracy') {
    return this.client.get('/seasonal-patterns/compare-patterns', {
      params: { pattern_ids: patternIds, comparison_metric: comparisonMetric }
    });
  }

  // Pattern Dashboard
  async getPatternDashboard() {
    return this.client.get('/seasonal-patterns/dashboard');
  }

  // ==================== Learning and Optimization System ====================

  // 17.1 User Decision Tracking
  async getUserDecisions(userId?: string, decisionType?: string, startDate?: string, endDate?: string, limit: number = 50) {
    return this.client.get('/learning/decisions', {
      params: { user_id: userId, decision_type: decisionType, start_date: startDate, end_date: endDate, limit }
    });
  }

  async getDecisionPatterns(userId?: string, patternType: string = 'all') {
    return this.client.get('/learning/decision-patterns', {
      params: { user_id: userId, pattern_type: patternType }
    });
  }

  // 17.2 Pattern Recognition Engine
  async detectPatterns(patternCategory?: string, minConfidence: number = 0.7) {
    return this.client.get('/learning/patterns', {
      params: { pattern_category: patternCategory, min_confidence: minConfidence }
    });
  }

  // 17.3 Recommendation Improvement
  async getRecommendationPerformance(recommendationType?: string, timePeriodDays: number = 30) {
    return this.client.get('/learning/recommendation-performance', {
      params: { recommendation_type: recommendationType, time_period_days: timePeriodDays }
    });
  }

  // 17.4 A/B Testing Framework
  async getABTests(status?: string, testType?: string) {
    return this.client.get('/learning/ab-tests', {
      params: { status, test_type: testType }
    });
  }

  async createABTest(testConfig: any) {
    return this.client.post('/learning/ab-tests', testConfig);
  }

  // 17.5 Knowledge Base Evolution
  async getKnowledgeBase(category?: string, minRelevanceScore: number = 0.7) {
    return this.client.get('/learning/knowledge-base', {
      params: { category, min_relevance_score: minRelevanceScore }
    });
  }

  async contributeToKnowledgeBase(contribution: any) {
    return this.client.post('/learning/knowledge-base/contribute', contribution);
  }

  // Learning Dashboard
  async getLearningDashboard() {
    return this.client.get('/learning/dashboard');
  }

  // ==================== User Management ====================

  // Get all users
  async getUsers() {
    return this.client.get('/user-management/users');
  }

  // Get user by ID
  async getUserById(userId: string) {
    return this.client.get(`/user-management/users/${userId}`);
  }

  // Update user
  async updateUser(userId: string, userData: {
    email?: string;
    full_name?: string;
    role?: string;
    is_active?: boolean;
    password?: string;
  }) {
    return this.client.put(`/user-management/users/${userId}`, userData);
  }

  // Delete user
  async deleteUser(userId: string) {
    return this.client.delete(`/user-management/users/${userId}`);
  }

  // ==================== ENHANCED EXECUTIVE DASHBOARD (NEW) ====================

  // Get filter options for dropdowns
  async getExecutiveFilterOptions() {
    return this.client.get('/executive-dashboard/filter-options');
  }

  // Get revenue metrics with filters
  async getExecutiveRevenue(params?: {
    countries?: string;
    retailers?: string;
    brands?: string;
    stores?: string;
    categories?: string;
    time_period?: string;
    start_date?: string;
    end_date?: string;
  }) {
    return this.client.get('/executive-dashboard/revenue', { params });
  }

  // Get fill rate metrics
  async getExecutiveFillRate(params?: {
    countries?: string;
    retailers?: string;
    brands?: string;
    stores?: string;
    start_date?: string;
    end_date?: string;
  }) {
    return this.client.get('/executive-dashboard/fill-rate', { params });
  }

  // Get inventory value breakdown
  async getExecutiveInventoryValue(params?: {
    countries?: string;
    retailers?: string;
    brands?: string;
    stores?: string;
  }) {
    return this.client.get('/executive-dashboard/inventory-value', { params });
  }

  // Get stock coverage metrics
  async getExecutiveStockCoverage(params?: {
    countries?: string;
    retailers?: string;
    brands?: string;
    stores?: string;
    unit?: 'days' | 'months';
  }) {
    return this.client.get('/executive-dashboard/stock-coverage', { params });
  }

  // Get ABC stock mix
  async getExecutiveABCMix(params?: {
    countries?: string;
    retailers?: string;
    brands?: string;
    stores?: string;
  }) {
    return this.client.get('/executive-dashboard/abc-mix', { params });
  }

  // ==================== SPRINT 7: UNIFIED DASHBOARD ====================

  // Get filter options for unified dashboard
  async getUnifiedDashboardFilters() {
    return this.client.get('/executive-dashboard/filter-options');
  }

  // Get comprehensive unified dashboard metrics
  async getUnifiedDashboardMetrics(filters: {
    countries?: string[];
    cities?: string[];
    retailers?: string[];
    brands?: string[];
    stores?: string[];
    categories?: string[];
    subcategories?: string[];
    abc_classifications?: string[];
    lifecycle_stages?: string[];
    date_from?: string;
    date_to?: string;
  }, period: 'week' | 'month' | 'quarter' | 'year' = 'month') {
    return this.client.get('/executive-dashboard/complete-metrics', {
      params: { ...filters, period }
    });
  }

  // Get trending metrics (week/month over week/month comparison)
  async getUnifiedDashboardTrending(params?: {
    period?: 'week' | 'month';
    countries?: string;
    retailers?: string;
    brands?: string;
  }) {
    return this.client.get('/executive-dashboard/trending', { params });
  }

  // ==================== ENHANCED STOCKOUT TRACKING (NEW) ====================

  // Get stockouts with root cause analysis
  async getStockoutsWithRootCause(params?: {
    countries?: string;
    retailers?: string;
    brands?: string;
    stores?: string;
    categories?: string;
    cities?: string;
    abc_classifications?: string;
    root_cause?: string;
    is_resolved?: boolean;
    start_date?: string;
    end_date?: string;
    limit?: number;
    offset?: number;
  }) {
    return this.client.get('/stockout-tracking/enhanced/list', { params });
  }

  // Analyze root cause of a specific stockout
  async analyzeStockoutRootCause(stockoutId: string) {
    return this.client.get(`/stockout-tracking/enhanced/${stockoutId}/root-cause`);
  }

  // Create new stockout tracking record
  async createStockoutTracking(data: {
    product_id: string;
    store_id: string;
    stockout_start_date: string;
    stockout_end_date?: string;
    orders_pending?: boolean;
    previous_orders_count?: number;
    unfulfilled_orders_count?: number;
  }) {
    return this.client.post('/stockout-tracking/enhanced/create', null, {
      params: data
    });
  }

  // Get stockout analytics by root cause
  async getStockoutAnalyticsByRootCause(params?: {
    countries?: string;
    retailers?: string;
    brands?: string;
    stores?: string;
    categories?: string;
    cities?: string;
    abc_classifications?: string;
    start_date?: string;
    end_date?: string;
  }) {
    return this.client.get('/stockout-tracking/enhanced/analytics/by-root-cause', { params });
  }

  async getMonthlyStockoutValue(params?: {
    countries?: string;
    retailers?: string;
    brands?: string;
    stores?: string;
    categories?: string;
    cities?: string;
    abc_classifications?: string;
    start_date?: string;
    end_date?: string;
  }) {
    return this.client.get('/stockout-tracking/enhanced/analytics/monthly-value', { params });
  }

  // ==================== SEASONALITY PATTERNS DATABASE (NEW) ====================

  // Get all seasonality patterns
  async getSeasonalityPatternsList(params?: {
    category?: string;
    subcategory?: string;
    brand?: string;
    is_active?: boolean;
    is_default?: boolean;
    limit?: number;
    offset?: number;
  }) {
    return this.client.get('/seasonality-configuration/patterns', { params });
  }

  // Create new seasonality pattern
  async createSeasonalityPatternDB(data: {
    pattern_name: string;
    category?: string;
    subcategory?: string;
    brand?: string;
    monthly_distribution: {
      jan: number;
      feb: number;
      mar: number;
      apr: number;
      may: number;
      jun: number;
      jul: number;
      aug: number;
      sep: number;
      oct: number;
      nov: number;
      dec: number;
    };
    is_default?: boolean;
  }) {
    return this.client.post('/seasonality-configuration/patterns', data);
  }

  // Get specific seasonality pattern
  async getSeasonalityPatternDB(patternId: string) {
    return this.client.get(`/seasonality-configuration/patterns/${patternId}`);
  }

  // Update seasonality pattern
  async updateSeasonalityPatternDB(patternId: string, data: {
    pattern_name?: string;
    category?: string;
    subcategory?: string;
    brand?: string;
    monthly_distribution?: any;
    is_active?: boolean;
    is_default?: boolean;
  }) {
    return this.client.patch(`/seasonality-configuration/patterns/${patternId}`, data);
  }

  // Delete seasonality pattern
  async deleteSeasonalityPatternDB(patternId: string) {
    return this.client.delete(`/seasonality-configuration/patterns/${patternId}`);
  }

  // Get default pattern for category
  async getDefaultSeasonalityPattern(params: {
    category: string;
    subcategory?: string;
    brand?: string;
  }) {
    return this.client.get(`/seasonality-configuration/patterns/default/${params.category}`, {
      params: {
        subcategory: params.subcategory,
        brand: params.brand,
      }
    });
  }

  // Get categories list
  async getSeasonalityCategories() {
    return this.client.get('/seasonality-configuration/categories');
  }

  // Get subcategories list
  async getSeasonalitySubcategories(category?: string) {
    return this.client.get('/seasonality-configuration/subcategories', {
      params: { category }
    });
  }

  // Get brands list
  async getSeasonalityBrands(category?: string) {
    return this.client.get('/seasonality-configuration/brands', {
      params: { category }
    });
  }

  // ==================== SUPPLIER CONSTRAINTS MANAGEMENT ====================

  // List supplier constraints
  async listSupplierConstraints(params?: {
    supplier_name?: string;
    product_id?: string;
    stock_origin_location?: string;
    is_dangerous_goods?: boolean;
    is_active?: boolean;
    limit?: number;
    offset?: number;
  }) {
    return this.client.get('/supplier-constraints/constraints', { params });
  }

  // Create supplier constraint
  async createSupplierConstraint(data: {
    supplier_name: string;
    moq_units?: number;
    lead_time_days: number;
    stock_origin_location?: string;
    order_frequency?: string;
    order_frequency_days?: number;
    historical_fill_rate?: number;
    average_fulfillment_rate?: number;
    is_dangerous_goods?: boolean;
    is_inflammable?: boolean;
    product_id?: string;
  }) {
    return this.client.post('/supplier-constraints/constraints', data);
  }

  // Get specific supplier constraint
  async getSupplierConstraint(constraintId: string) {
    return this.client.get(`/supplier-constraints/constraints/${constraintId}`);
  }

  // Update supplier constraint
  async updateSupplierConstraint(constraintId: string, data: {
    supplier_name?: string;
    moq_units?: number;
    lead_time_days?: number;
    stock_origin_location?: string;
    order_frequency?: string;
    order_frequency_days?: number;
    historical_fill_rate?: number;
    average_fulfillment_rate?: number;
    is_dangerous_goods?: boolean;
    is_inflammable?: boolean;
    is_active?: boolean;
  }) {
    return this.client.patch(`/supplier-constraints/constraints/${constraintId}`, data);
  }

  // Delete supplier constraint
  async deleteSupplierConstraint(constraintId: string, hardDelete: boolean = false) {
    return this.client.delete(`/supplier-constraints/constraints/${constraintId}`, {
      params: { hard_delete: hardDelete }
    });
  }

  // Get suppliers list
  async getSuppliersList() {
    return this.client.get('/supplier-constraints/suppliers/list');
  }

  // Get stock origins list
  async getStockOriginsList() {
    return this.client.get('/supplier-constraints/stock-origins/list');
  }

  // Bulk create supplier constraints
  async bulkCreateSupplierConstraints(constraints: any[]) {
    return this.client.post('/supplier-constraints/constraints/bulk-create', constraints);
  }

  // ==================== FORECAST MANAGEMENT (365-DAY FORECASTING) ====================

  // Generate 365-day forecast (legacy forecast management)
  async generateForecastManagement365(data: {
    product_id: string;
    store_id: string;
    method?: string;
    start_date?: string;
  }) {
    return this.client.post('/forecast-management/generate-365', data);
  }

  // View existing 365-day forecasts
  async view365DayForecasts(params?: {
    product_id?: string;
    store_id?: string;
    start_date?: string;
    end_date?: string;
  }) {
    return this.client.get('/forecast-management/view-365', { params });
  }

  // Create forecast override
  async createForecastOverride(data: {
    product_id: string;
    store_id: string;
    forecast_date: string;
    override_forecast: number;
    override_reason?: string;
    override_category?: string;
  }) {
    return this.client.post('/forecast-management/override/create', data);
  }

  // List forecast overrides
  async listForecastOverrides(params?: {
    product_id?: string;
    store_id?: string;
    start_date?: string;
    end_date?: string;
  }) {
    return this.client.get('/forecast-management/override/list', { params });
  }

  // Delete forecast override
  async deleteForecastOverride(overrideId: string) {
    return this.client.delete(`/forecast-management/override/${overrideId}`);
  }

  // Bulk create/update forecast overrides
  async bulkCreateOverrides(overrides: Array<{
    product_id: string;
    store_id: string;
    forecast_date: string;
    override_forecast: number;
    override_reason?: string;
    override_category?: string;
  }>) {
    return this.client.post('/forecast-management/override/bulk-create', { overrides });
  }

  // Bulk delete overrides (reset to initial forecast)
  async bulkDeleteOverrides(params?: {
    brand?: string;
    category?: string;
    city?: string;
    store_id?: string;
    product_id?: string;
  }) {
    return this.client.post('/forecast-management/override/bulk-delete', null, { params });
  }

  // Get overrides matching projection grid filters
  async getProjectionOverrides(params?: {
    brand?: string;
    category?: string;
    city?: string;
    store_id?: string;
    product_family?: string;
    sku?: string;
    months?: number;
  }) {
    return this.client.get('/forecast-management/override/by-projection', { params });
  }

  // Cannibalization CRUD
  async getCannibalizationRecords(params?: {
    new_product_id?: string;
    new_product_brand?: string;
    is_active?: boolean;
    limit?: number;
    offset?: number;
  }) {
    return this.client.get('/cannibalization/', { params });
  }

  async createCannibalizationRecord(data: {
    new_product_id?: string;
    new_product_sku?: string;
    new_product_name?: string;
    new_product_category?: string;
    new_product_brand?: string;
    cannibalized_product_id?: string;
    cannibalized_product_sku?: string;
    cannibalized_product_name?: string;
    cannibalized_category?: string;
    cannibalized_brand?: string;
    launch_date: string;
    end_date?: string;
    cannibalization_ratio: number;
    notes?: string;
    is_cross_brand?: boolean;
  }) {
    return this.client.post('/cannibalization/', data);
  }

  async deleteCannibalizationRecord(cannibalizationId: string) {
    return this.client.delete(`/cannibalization/${cannibalizationId}`);
  }

  // New Product Forecast CRUD
  async getNewProductForecasts(params?: {
    new_product_id?: string;
    benchmark_product_id?: string;
    is_active?: boolean;
    limit?: number;
    offset?: number;
  }) {
    return this.client.get('/new-product-forecast/', { params });
  }

  async createNewProductForecast(data: {
    new_product_id?: string;
    new_product_sku?: string;
    new_product_name?: string;
    new_product_brand?: string;
    new_product_category?: string;
    benchmark_product_id?: string;
    benchmark_product_sku?: string;
    benchmark_product_name?: string;
    benchmark_product_brand?: string;
    benchmark_product_category?: string;
    launch_date: string;
    adjustment_pct: number;
    monthly_forecast?: any[];
    config_name?: string;
    notes?: string;
  }) {
    return this.client.post('/new-product-forecast/', data);
  }

  async deleteNewProductForecast(forecastId: string) {
    return this.client.delete(`/new-product-forecast/${forecastId}`);
  }

  async getBenchmarkMonthlySales(productId: string, months: number = 12) {
    return this.client.get(`/new-product-forecast/benchmark/${productId}/monthly-sales`, {
      params: { months },
    });
  }

  async getStoreProfileForecast(data: {
    benchmark_product_id: string;
    months?: number;
    adjustment_pct?: number;
    target_store_types?: string[] | null;
    new_product_category?: string | null;
  }) {
    return this.client.post('/new-product-forecast/store-profile-forecast', data);
  }

  // Module settings (generic key-value store)
  async getSettings(settingType: string) {
    return this.client.get(`/settings/${settingType}`);
  }

  async saveSettings(settingType: string, data: any) {
    return this.client.post(`/settings/${settingType}`, data);
  }

  // Get forecast accuracy summary
  async getForecastAccuracySummary(params?: {
    countries?: string;
    retailers?: string;
    brands?: string;
    stores?: string;
    start_date?: string;
    end_date?: string;
  }) {
    return this.client.get('/forecast-management/accuracy/summary', { params });
  }

  // Get forecast accuracy by product (TOP N worst/best performers)
  async getForecastAccuracyByProduct(params?: {
    top_n?: number;
    worst_performers?: boolean;
    countries?: string;
    retailers?: string;
    brands?: string;
    stores?: string;
  }) {
    return this.client.get('/forecast-management/accuracy/by-product', { params });
  }

  // Get forecast accuracy by brand
  async getForecastAccuracyByBrand(params?: {
    countries?: string;
    retailers?: string;
    brands?: string;
    stores?: string;
  }) {
    return this.client.get('/forecast-management/accuracy/by-brand', { params });
  }

  // ========================================================================
  // STOCKOUT ROOT CAUSE ANALYSIS
  // ========================================================================

  // Detect and analyze current stockouts
  async detectAndAnalyzeStockouts() {
    return this.client.post('/stockout-root-cause/detect-and-analyze');
  }

  // List all stockouts with root cause
  async listStockoutsWithRootCause(params?: {
    start_date?: string;
    end_date?: string;
    store_id?: string;
    product_id?: string;
    root_cause?: string;
    is_resolved?: boolean;
    limit?: number;
    offset?: number;
  }) {
    return this.client.get('/stockout-root-cause/list', { params });
  }

  // Get stockout analytics
  async getStockoutAnalytics(params?: {
    start_date?: string;
    end_date?: string;
    country?: string;
    brand?: string;
  }) {
    return this.client.get('/stockout-root-cause/analytics', { params });
  }

  // Update stockout action
  async updateStockoutAction(stockoutId: string, data: {
    action_taken: string;
    action_status: string;
  }) {
    return this.client.put(`/stockout-root-cause/${stockoutId}/action`, data);
  }

  // Resolve stockout
  async resolveStockout(stockoutId: string) {
    return this.client.put(`/stockout-root-cause/${stockoutId}/resolve`);
  }

  // ========================================================================
  // EXECUTIVE DASHBOARD - STOCK COVERAGE & ABC MIX
  // ========================================================================

  // Get ABC stock mix
  async getAbcStockMix(params?: {
    countries?: string;
    retailers?: string;
    brands?: string;
    stores?: string;
    categories?: string;
  }) {
    return this.client.get('/executive-dashboard/abc-stock-mix', { params });
  }

  // Get stock aging analysis
  async getStockAgingAnalysis(params?: {
    countries?: string;
    retailers?: string;
    brands?: string;
    stores?: string;
    categories?: string;
    dead_stock_threshold_days?: number;
  }) {
    return this.client.get('/executive-dashboard/stock-aging', { params });
  }

  // Aliases for backward compatibility (prefer getExecutiveFillRate and getExecutiveStockCoverage)
  async getStockCoverageMetrics(params?: Record<string, unknown>) {
    return this.getExecutiveStockCoverage(params as any);
  }

  async getFillRateKpi(params?: Record<string, unknown>) {
    return this.getExecutiveFillRate(params as any);
  }

  async getServiceLevelDetail(params: {
    start_date: string;
    end_date: string;
    store_id?: string;
    brand?: string;
  }) {
    return this.client.get('/fill-rate/service-level-detail', { params });
  }

  async getServiceLevelMonthly(params: {
    start_date: string;
    end_date: string;
    store_id?: string;
    brand?: string;
  }) {
    return this.client.get('/fill-rate/service-level-monthly', { params });
  }

  // ==================== STOCK AGING ANALYSIS ====================

  // Get stock aging dashboard
  async getStockAgingDashboard(params?: {
    countries?: string;
    retailers?: string;
    brands?: string;
    stores?: string;
    age_bucket?: string;
    start_date?: string;
    end_date?: string;
  }) {
    return this.client.get('/stock-aging/dashboard', { params });
  }

  // Get detailed stock aging records
  async getDetailedStockAging(params?: {
    countries?: string;
    retailers?: string;
    brands?: string;
    stores?: string;
    age_bucket?: string;
    is_slow_moving?: boolean;
    is_obsolete?: boolean;
    sort_by?: string;
    limit?: number;
    offset?: number;
  }) {
    return this.client.get('/stock-aging/detailed', { params });
  }

  // Get slow-moving inventory (60+ days)
  async getSlowMovingInventory(params?: {
    countries?: string;
    retailers?: string;
    brands?: string;
    stores?: string;
    limit?: number;
    offset?: number;
  }) {
    return this.client.get('/stock-aging/slow-moving', { params });
  }

  // Get obsolete inventory (180+ days)
  async getObsoleteInventory(params?: {
    countries?: string;
    retailers?: string;
    brands?: string;
    stores?: string;
    limit?: number;
    offset?: number;
  }) {
    return this.client.get('/stock-aging/obsolete', { params });
  }

  // Calculate aging for all inventory
  async calculateStockAging() {
    return this.client.post('/stock-aging/calculate');
  }

  // Create stock aging record
  async createStockAgingRecord(data: {
    product_id: string;
    store_id: string;
    batch_reference?: string;
    receipt_date: string;
    original_quantity: number;
    current_quantity: number;
    unit_cost?: number;
  }) {
    return this.client.post('/stock-aging/record', data);
  }

  // Update stock aging record
  async updateStockAgingRecord(agingId: string, data: {
    current_quantity?: number;
    is_slow_moving?: boolean;
    is_obsolete?: boolean;
    markdown_required?: boolean;
    clearance_recommended?: boolean;
  }) {
    return this.client.patch(`/stock-aging/record/${agingId}`, data);
  }

  // ==================== RECOVERY PLAN EVALUATION ====================

  // Get recovery plan dashboard
  async getRecoveryPlanDashboard(params?: {
    countries?: string;
    retailers?: string;
    brands?: string;
    stores?: string;
    start_date?: string;
    end_date?: string;
  }) {
    return this.client.get('/recovery-plans/dashboard', { params });
  }

  // List recovery plans
  async listRecoveryPlans(params?: {
    countries?: string;
    retailers?: string;
    brands?: string;
    stores?: string;
    recovery_strategy?: string;
    plan_executed?: boolean;
    recovery_success?: boolean;
    limit?: number;
    offset?: number;
  }) {
    return this.client.get('/recovery-plans/list', { params });
  }

  // Create recovery plan
  async createRecoveryPlan(data: {
    product_id: string;
    store_id: string;
    stockout_date: string;
    stockout_quantity: number;
    lost_sales_value?: number;
    recovery_strategy: string;
    target_recovery_date?: string;
    emergency_order_quantity?: number;
    transfer_from_store?: string;
    substitute_product_id?: string;
    root_cause?: string;
    stockout_tracking_id?: string;
  }) {
    return this.client.post('/recovery-plans/create', data);
  }

  // Execute recovery plan
  async executeRecoveryPlan(recoveryId: string, data: {
    execution_date?: string;
    notes?: string;
  }) {
    return this.client.patch(`/recovery-plans/${recoveryId}/execute`, data);
  }

  // Evaluate recovery plan
  async evaluateRecoveryPlan(recoveryId: string, data: {
    actual_recovery_date: string;
    recovery_success: boolean;
    recovery_rate?: number;
    customer_satisfaction_impact?: string;
    preventive_action_taken?: string;
    notes?: string;
  }) {
    return this.client.patch(`/recovery-plans/${recoveryId}/evaluate`, data);
  }

  // Get specific recovery plan
  async getRecoveryPlan(recoveryId: string) {
    return this.client.get(`/recovery-plans/${recoveryId}`);
  }

  // Delete recovery plan
  async deleteRecoveryPlan(recoveryId: string) {
    return this.client.delete(`/recovery-plans/${recoveryId}`);
  }

  // ==================== ENHANCED ORDER HISTORY ====================

  // Get detailed order history for stockout investigation
  async getDetailedOrderHistory(storeId: string, productId: string, lookbackDays: number = 90) {
    return this.client.get(`/order-history/detailed/${storeId}/${productId}`, {
      params: { lookback_days: lookbackDays }
    });
  }

  // Get order timeline for a product at a store
  async getOrderTimeline(storeId: string, productId: string, startDate: string, endDate: string) {
    return this.client.get(`/order-history/timeline/${storeId}/${productId}`, {
      params: { start_date: startDate, end_date: endDate }
    });
  }

  // Compare order history across stores for the same product
  async compareOrderHistoryAcrossStores(productId: string, lookbackDays: number = 90) {
    return this.client.get(`/order-history/compare-stores/${productId}`, {
      params: { lookback_days: lookbackDays }
    });
  }

  // Get order history summary
  async getOrderHistorySummary(params?: {
    store_id?: string;
    product_id?: string;
    country?: string;
    brand?: string;
    lookback_days?: number;
  }) {
    return this.client.get('/order-history/summary', { params });
  }

  // ==================== SPRINT 3: DASHBOARD CONSOLIDATION ====================

  // Get lifecycle breakdown
  async getLifecycleBreakdown(params?: {
    countries?: string;
    retailers?: string;
    brands?: string;
    stores?: string;
  }) {
    return this.client.get('/executive-dashboard/lifecycle-breakdown', { params });
  }

  // ==================== SPRINT 4: FORECASTING IMPROVEMENTS ====================

  // M-1 Forecast Rule
  async calculateM1Forecast(params: {
    product_id: string;
    store_id: string;
    forecast_month?: string;  // YYYY-MM format
  }) {
    return this.client.post('/forecast-improvements/m1-forecast/calculate', null, { params });
  }

  async generateAndSaveM1Forecast(params: {
    product_id: string;
    store_id: string;
    forecast_month?: string;
  }) {
    return this.client.post('/forecast-improvements/m1-forecast/generate-and-save', null, { params });
  }

  // TOP 20/50 Filters
  async getTopProducts(params: {
    percentage: 20 | 50;
    store_id?: string;
    country?: string;
    retailer?: string;
    brand?: string;
    days_back?: number;
  }) {
    const { percentage, ...queryParams } = params;
    return this.client.get(`/forecast-improvements/top-products/${percentage}`, { params: queryParams });
  }

  async getTopProductsAnalytics(params?: {
    store_id?: string;
    country?: string;
    retailer?: string;
    brand?: string;
    days_back?: number;
  }) {
    return this.client.get('/forecast-improvements/top-products-analytics', { params });
  }

  // 365-Day Horizon Validation
  async validate365DayForecast(params: {
    product_id: string;
    store_id: string;
    method?: 'prophet' | 'moving_average';
  }) {
    return this.client.post('/forecast-improvements/validate-365day', null, { params });
  }

  async generate365DayForecast(params: {
    product_id: string;
    store_id: string;
    method?: 'prophet' | 'moving_average';
    save_to_db?: boolean;
  }) {
    return this.client.post('/forecast-improvements/generate-365day', null, { params });
  }

  // Sprint 4 Dashboard
  async getSprint4Dashboard(params?: {
    product_id?: string;
    store_id?: string;
    country?: string;
    retailer?: string;
    brand?: string;
  }) {
    return this.client.get('/forecast-improvements/sprint4-dashboard', { params });
  }

  // ==================== SPRINT 5: SCENARIO PLANNING ====================

  // Store Network Changes
  async analyzeStoreNetworkImpact(data: {
    changes: Array<{
      store_id: string;
      change_type: 'closure' | 'opening' | 'relocation';
      effective_date: string;
      affected_stores?: string[];
      transfer_percentage?: number;
      cannibalization_percentage?: number;
      reference_stores?: string[];
    }>;
    start_date?: string;
    end_date?: string;
  }) {
    return this.client.post('/scenario-planning/store-network/analyze', data.changes, {
      params: {
        start_date: data.start_date,
        end_date: data.end_date
      }
    });
  }

  async simulateStoreNetworkScenario(params: {
    change_type: 'closure' | 'opening' | 'relocation';
    store_id: string;
    affected_stores?: string;
    transfer_percentage?: number;
    start_date?: string;
    end_date?: string;
  }) {
    return this.client.get('/scenario-planning/store-network/simulate', { params });
  }

  // Supply Disruption
  async analyzeSupplyDisruption(data: {
    disruptions: Array<{
      supplier_id: string;
      disruption_type: 'delay' | 'shortage' | 'quality';
      severity: number;
      start_date: string;
      end_date: string;
      affected_products?: string[];
      alternative_suppliers?: string[];
    }>;
    products?: string;
    stores?: string;
  }) {
    return this.client.post('/scenario-planning/supply-disruption/analyze', data.disruptions, {
      params: {
        products: data.products,
        stores: data.stores
      }
    });
  }

  async simulateSupplyDisruption(params: {
    supplier_id: string;
    disruption_type: 'delay' | 'shortage' | 'quality';
    severity: number;
    start_date: string;
    end_date: string;
    affected_products?: string;
    alternative_suppliers?: string;
  }) {
    return this.client.get('/scenario-planning/supply-disruption/simulate', { params });
  }

  // Category Multipliers
  async applyCategoryMultipliers(data: {
    multipliers: Array<{
      category: string;
      sub_category?: string;
      multiplier: number;
      reason?: string;
      start_date?: string;
      end_date?: string;
    }>;
    start_date?: string;
    end_date?: string;
    stores?: string;
  }) {
    return this.client.post('/scenario-planning/category-multipliers/apply', data.multipliers, {
      params: {
        start_date: data.start_date,
        end_date: data.end_date,
        stores: data.stores
      }
    });
  }

  async simulateCategoryMultiplier(params: {
    category: string;
    multiplier: number;
    sub_category?: string;
    reason?: string;
    start_date?: string;
    end_date?: string;
    stores?: string;
  }) {
    return this.client.get('/scenario-planning/category-multipliers/simulate', { params });
  }

  // Combined Scenarios
  async createScenarioPlan(data: {
    scenario_name: string;
    store_changes?: any[];
    supply_disruptions?: any[];
    category_multipliers?: any[];
    start_date?: string;
    end_date?: string;
  }) {
    return this.client.post('/scenario-planning/scenarios/create', data);
  }

  async getScenarioDashboard(params?: {
    start_date?: string;
    end_date?: string;
  }) {
    return this.client.get('/scenario-planning/scenarios/dashboard', { params });
  }

  async getScenarioTemplates() {
    return this.client.get('/scenario-planning/scenarios/templates');
  }

  // ==================== SPRINT 6: ADVANCED FEATURES ====================

  // Cross-Brand Cannibalization
  async analyzeBrandCannibalization(data: {
    brands?: string[];
    stores?: string[];
    start_date?: string;
    end_date?: string;
    min_correlation?: number;
  }) {
    return this.client.post('/advanced-features/cannibalization/analyze', data);
  }

  async getBrandCannibalizationMatrix(params?: {
    brands?: string;
    stores?: string;
    start_date?: string;
    end_date?: string;
    min_correlation?: number;
  }) {
    return this.client.get('/advanced-features/cannibalization/matrix', { params });
  }

  async getBrandVulnerabilities(params?: {
    brands?: string;
    stores?: string;
    start_date?: string;
    end_date?: string;
  }) {
    return this.client.get('/advanced-features/cannibalization/vulnerabilities', { params });
  }

  // Multi-Scenario Comparison
  async compareAdvancedScenarios(data: {
    scenarios: Array<{
      scenario_name: string;
      components?: {
        store_changes?: any[];
        supply_disruptions?: any[];
        category_multipliers?: any[];
      };
      combined_impact?: {
        total_forecast_impact?: number;
        risk_assessment?: string;
      };
    }>;
  }) {
    return this.client.post('/advanced-features/scenarios/compare', data);
  }

  async getBestCaseScenario(data: {
    scenarios: any[];
  }) {
    return this.client.post('/advanced-features/scenarios/best-case', data);
  }

  async getWorstCaseScenario(data: {
    scenarios: any[];
  }) {
    return this.client.post('/advanced-features/scenarios/worst-case', data);
  }

  async analyzeScenarioDivergence(data: {
    scenarios: any[];
  }) {
    return this.client.post('/advanced-features/scenarios/divergence', data);
  }

  // Stockout Analytics Dashboard
  async getStockoutDashboard(params?: {
    stores?: string;
    products?: string;
    start_date?: string;
    end_date?: string;
  }) {
    return this.client.get('/advanced-features/stockout/dashboard', { params });
  }

  async getStockoutTrends(params?: {
    stores?: string;
    products?: string;
    start_date?: string;
    end_date?: string;
  }) {
    return this.client.get('/advanced-features/stockout/trends', { params });
  }

  async getTopStockoutProducts(params?: {
    limit?: number;
    stores?: string;
    start_date?: string;
    end_date?: string;
  }) {
    return this.client.get('/advanced-features/stockout/top-products', { params });
  }

  async getTopStockoutStores(params?: {
    limit?: number;
    products?: string;
    start_date?: string;
    end_date?: string;
  }) {
    return this.client.get('/advanced-features/stockout/top-stores', { params });
  }

  async getStockoutRootCauses(params?: {
    stores?: string;
    products?: string;
    start_date?: string;
    end_date?: string;
  }) {
    return this.client.get('/advanced-features/stockout/root-causes', { params });
  }

  async getStockoutCostImpact(params?: {
    stores?: string;
    products?: string;
    start_date?: string;
    end_date?: string;
  }) {
    return this.client.get('/advanced-features/stockout/cost-impact', { params });
  }

  // Health check for advanced features
  async checkAdvancedFeaturesHealth() {
    return this.client.get('/advanced-features/health');
  }

  // Backward-compatible aliases (prefer getExecutiveKPIs, getExecutiveRevenue, etc.)
  async getDashboardKPIs(params?: Record<string, unknown>) {
    return this.getExecutiveKPIs(params as any);
  }

  async getDashboardRevenue(params?: Record<string, unknown>) {
    return this.getExecutiveRevenue(params as any);
  }

  // Backward-compatible alias for getForecastResults
  async getForecastData(params: { product_id: string; store_id: string; limit?: number }) {
    return this.getForecastResults(params.product_id, params.store_id, params.limit);
  }

  // Backward-compatible alias for getOrderHistory
  async getReplenishmentOrders(params?: { store_id?: string; product_id?: string; limit?: number }) {
    return this.getOrderHistory(params);
  }

  // ========== Filter Methods ==========

  async getCountries() {
    return this.client.get<string[]>('/filters/countries');
  }

  async getBrands() {
    return this.client.get<string[]>('/filters/brands');
  }

  async getCategories() {
    return this.client.get<string[]>('/filters/categories');
  }

  async getSubcategories(category?: string) {
    const params = category ? { category } : {};
    return this.client.get<string[]>('/filters/subcategories', { params });
  }

  async getRetailers() {
    return this.client.get<string[]>('/filters/retailers');
  }

  async getCities(country?: string) {
    const params = country ? { country } : {};
    return this.client.get<string[]>('/filters/cities', { params });
  }

  /**
   * Fetch all filter options in a single request.
   * Supports optional cascading query params (country, category, retailer).
   */
  async getFilterOptions(queryParams?: {
    country?: string;
    category?: string;
    retailer?: string;
  }) {
    const params = queryParams ?? {};
    return this.client.get('/filters/options', { params });
  }

  /**
   * Fetch stores for filter dropdown.
   * Optionally filter by country and/or retailer.
   */
  async getFilterStores(queryParams?: {
    country?: string;
    retailer?: string;
  }) {
    const params = queryParams ?? {};
    return this.client.get('/filters/stores', { params });
  }

  // ==================== Onboarding Wizard ====================

  async getOnboardingProgress() {
    return this.client.get('/onboarding/progress');
  }

  async startOnboarding() {
    return this.client.post('/onboarding/start');
  }

  async completeOnboardingStep(stepNumber: number, data: Record<string, any>) {
    return this.client.put(`/onboarding/step/${stepNumber}`, { data });
  }

  async completeOnboarding() {
    return this.client.post('/onboarding/complete');
  }

  // ==================== Undo/Redo Change Management ====================

  async recordChange(data: {
    entity_type: string;
    entity_id: string;
    field_name: string;
    old_value?: string;
    new_value: string;
    change_type?: string;
    description?: string;
  }) {
    return this.client.post('/changes/record', data);
  }

  async undoChange(changeId: string) {
    return this.client.post(`/changes/undo/${changeId}`);
  }

  async redoChange(changeId: string) {
    return this.client.post(`/changes/redo/${changeId}`);
  }

  async getChangeHistory(params?: {
    page?: number;
    page_size?: number;
    entity_type?: string;
    status?: string;
  }) {
    return this.client.get('/changes/history', { params });
  }

  async bulkUndoChanges(changeIds: string[]) {
    return this.client.post('/changes/bulk-undo', { change_ids: changeIds });
  }

  // ==================== Chatbot FAQ ====================

  async askChatbot(question: string, maxResults: number = 5) {
    return this.client.post('/chatbot/ask', { question, max_results: maxResults });
  }

  async searchFAQ(query: string, limit: number = 10) {
    return this.client.get('/chatbot/faq/search', { params: { q: query, limit } });
  }

  async chatWithAssistant(message: string, conversationHistory?: Array<{ role: string; content: string }>) {
    return this.client.post('/chatbot/chat', { message, conversation_history: conversationHistory });
  }

  // ==================== DASHBOARD ANALYTICS ====================

  private buildAnalyticsQueryString(filters: any): string {
    const params = new URLSearchParams();
    if (filters.dateFrom) {
      params.append('date_from', filters.dateFrom instanceof Date ? filters.dateFrom.toISOString().split('T')[0] : filters.dateFrom);
    }
    if (filters.dateTo) {
      params.append('date_to', filters.dateTo instanceof Date ? filters.dateTo.toISOString().split('T')[0] : filters.dateTo);
    }
    if (filters.countries?.length) {
      filters.countries.forEach((c: string) => params.append('countries', c));
    }
    if (filters.brands?.length) {
      filters.brands.forEach((b: string) => params.append('brands', b));
    }
    if (filters.categories?.length) {
      filters.categories.forEach((c: string) => params.append('categories', c));
    }
    if (filters.retailers?.length) {
      filters.retailers.forEach((r: string) => { if (r) params.append('retailers', r); });
    }
    if (filters.stores?.length) {
      filters.stores.forEach((s: string) => { if (s) params.append('stores', s); });
    }
    if (filters.cities?.length) {
      filters.cities.forEach((c: string) => { if (c) params.append('cities', c); });
    }
    return params.toString();
  }

  async getDashboardCategoryBreakdown(filters: any = {}) {
    const qs = this.buildAnalyticsQueryString(filters);
    const url = `/dashboard-analytics/category-breakdown${qs ? '?' + qs : ''}`;
    const response = await this.client.get(url);
    return response.data;
  }

  async getDashboardRevenueByCountry(filters: any = {}) {
    const qs = this.buildAnalyticsQueryString(filters);
    const url = `/dashboard-analytics/revenue-by-country${qs ? '?' + qs : ''}`;
    const response = await this.client.get(url);
    return response.data;
  }

  async getDashboardTopBrands(filters: any = {}, limit: number = 10) {
    const qs = this.buildAnalyticsQueryString(filters);
    const params = qs ? `${qs}&limit=${limit}` : `limit=${limit}`;
    const response = await this.client.get(`/dashboard-analytics/top-brands?${params}`);
    return response.data;
  }

  async getDashboardSalesTrend(filters: any = {}, granularity: 'daily' | 'weekly' | 'monthly' = 'daily') {
    const qs = this.buildAnalyticsQueryString(filters);
    const params = qs ? `${qs}&granularity=${granularity}` : `granularity=${granularity}`;
    const response = await this.client.get(`/dashboard-analytics/sales-trend?${params}`);
    return response.data;
  }

  async getDashboardAllAnalytics(filters: any = {}, granularity: 'daily' | 'weekly' | 'monthly' = 'monthly') {
    const [categoryData, countryRevenue, topBrands, salesTrend] = await Promise.all([
      this.getDashboardCategoryBreakdown(filters),
      this.getDashboardRevenueByCountry(filters),
      this.getDashboardTopBrands(filters, 10),
      this.getDashboardSalesTrend(filters, granularity),
    ]);
    return { categoryData, countryRevenue, topBrands, salesTrend };
  }

  async getDashboardMonthlyComparison(filters: { brands?: string[]; retailers?: string[]; countries?: string[]; stores?: string[]; cities?: string[] } = {}) {
    const params = new URLSearchParams();
    if (filters.brands?.length) filters.brands.forEach(b => params.append('brands', b));
    if (filters.retailers?.length) filters.retailers.forEach(r => params.append('retailers', r));
    if (filters.countries?.length) filters.countries.forEach(c => params.append('countries', c));
    if (filters.stores?.length) filters.stores.forEach(s => params.append('stores', s));
    if (filters.cities?.length) filters.cities.forEach(c => params.append('cities', c));
    const qs = params.toString();
    const response = await this.client.get(`/dashboard-analytics/monthly-revenue-comparison${qs ? '?' + qs : ''}`);
    return response.data;
  }

  async getDashboardMonthlyStockByLifecycle(filters: { brands?: string[]; stores?: string[]; cities?: string[]; dateFrom?: string; dateTo?: string } = {}) {
    const params = new URLSearchParams();
    if (filters.brands?.length) filters.brands.forEach(b => params.append('brands', b));
    if (filters.stores?.length) filters.stores.forEach(s => params.append('stores', s));
    if (filters.cities?.length) filters.cities.forEach(c => params.append('cities', c));
    if (filters.dateFrom) params.append('date_from', filters.dateFrom);
    if (filters.dateTo) params.append('date_to', filters.dateTo);
    const qs = params.toString();
    const response = await this.client.get(`/dashboard-analytics/monthly-stock-by-lifecycle${qs ? '?' + qs : ''}`);
    return response.data;
  }

  async getDashboardMonthlyStockByAbc(filters: { brands?: string[]; stores?: string[]; cities?: string[]; dateFrom?: string; dateTo?: string } = {}) {
    const params = new URLSearchParams();
    if (filters.brands?.length) filters.brands.forEach(b => params.append('brands', b));
    if (filters.stores?.length) filters.stores.forEach(s => params.append('stores', s));
    if (filters.cities?.length) filters.cities.forEach(c => params.append('cities', c));
    if (filters.dateFrom) params.append('date_from', filters.dateFrom);
    if (filters.dateTo) params.append('date_to', filters.dateTo);
    const qs = params.toString();
    const response = await this.client.get(`/dashboard-analytics/monthly-stock-by-abc${qs ? '?' + qs : ''}`);
    return response.data;
  }

  async getDashboardProjectionSummary(filters: { brands?: string[]; stores?: string[]; countries?: string[]; cities?: string[] } = {}) {
    const params = new URLSearchParams();
    if (filters.brands?.length) filters.brands.forEach(b => params.append('brands', b));
    if (filters.stores?.length) filters.stores.forEach(s => params.append('stores', s));
    if (filters.countries?.length) filters.countries.forEach(c => params.append('countries', c));
    if (filters.cities?.length) filters.cities.forEach(c => params.append('cities', c));
    const qs = params.toString();
    const response = await this.client.get(`/dashboard-analytics/projection-summary${qs ? '?' + qs : ''}`);
    return response.data;
  }

  // ==================== SEASONALITY MANAGEMENT ====================

  async getSeasonalityProfiles() {
    const response = await this.client.get('/seasonality/profiles');
    return response.data;
  }

  async getSeasonalityDetectPatterns(productId?: string, lookbackMonths: number = 12) {
    const endpoint = productId
      ? `/seasonality/detect-patterns/${productId}`
      : '/seasonality/detect-patterns/all';
    const response = await this.client.get(endpoint, { params: { lookback_months: lookbackMonths } });
    return response.data;
  }

  async createSeasonalityProfile(data: any) {
    const response = await this.client.post('/seasonality/profiles', data);
    return response.data;
  }

  async updateSeasonalityProfile(profileId: string, data: any) {
    const response = await this.client.put(`/seasonality/profiles/${profileId}`, data);
    return response.data;
  }

  async deleteSeasonalityProfile(profileId: string) {
    const response = await this.client.delete(`/seasonality/profiles/${profileId}`);
    return response.data;
  }

  async applySeasonalAdjustment(data: { product_id: string; store_id?: string; adjustment_date: string }) {
    const response = await this.client.post('/seasonality/apply-adjustment', data);
    return response.data;
  }

  // ==================== STOCK TRANSFER SUGGESTIONS ====================

  async getTransferSuggestions(params?: {
    target_coverage_days?: number;
    cities?: string;
    brands?: string;
    categories?: string;
    stores?: string;
    abc_classifications?: string;
    limit?: number;
  }) {
    return this.client.get('/logistics/transfers/suggestions', { params });
  }

  async createStockTransfer(data: {
    product_id: string;
    from_store_id: string;
    to_store_id: string;
    recommended_qty: number;
    notes?: string;
  }) {
    return this.client.post('/logistics/transfers', data);
  }

  // ==================== ROOT CAUSE ANALYSIS (ANALYTICS) ====================

  async getAnalyticsRootCauseAnalysis(params?: any) {
    const response = await this.client.get('/analytics/root-cause-analysis', { params });
    return response.data;
  }

  async getAnalyticsRootCauseSummary(params?: any) {
    const response = await this.client.get('/analytics/root-cause-summary', { params });
    return response.data;
  }

  async getAnalyticsCriticalStockAnalysis(params?: any) {
    const response = await this.client.get('/analytics/critical-stock-analysis', { params });
    return response.data;
  }

  // ==================== MODULE 1: SAGE ERP INTEGRATION ====================

  async getSageConfig() {
    return this.client.get('/sage-integration/config');
  }
  async saveSageConfig(data: any) {
    return this.client.post('/sage-integration/config', data);
  }
  async testSageConnection(configId: string) {
    return this.client.post('/sage-integration/config/test-connection', { config_id: configId });
  }
  async transmitOrderToSage(orderId: string) {
    return this.client.post('/sage-integration/transmit-order', { order_id: orderId });
  }
  async bulkTransmitOrdersToSage(orderIds: string[]) {
    return this.client.post('/sage-integration/transmit-orders/bulk', { order_ids: orderIds });
  }
  async syncSageOrderStatuses() {
    return this.client.post('/sage-integration/sync/order-status');
  }
  async syncSageInventory() {
    return this.client.post('/sage-integration/sync/inventory');
  }
  async importSageSales(dateFrom?: string, dateTo?: string) {
    return this.client.post('/sage-integration/sync/sales', { date_from: dateFrom, date_to: dateTo });
  }
  async getSageTransmissions(params?: any) {
    return this.client.get('/sage-integration/transmissions', { params });
  }
  async retrySageTransmission(transmissionId: string) {
    return this.client.post(`/sage-integration/transmissions/${transmissionId}/retry`);
  }
  async getSageSyncLogs(params?: any) {
    return this.client.get('/sage-integration/sync-logs', { params });
  }
  async importInventoryCsv(file: File) {
    const formData = new FormData();
    formData.append('file', file);
    return this.client.post('/sage-integration/import/inventory-csv', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  }
  async importSalesCsv(file: File) {
    const formData = new FormData();
    formData.append('file', file);
    return this.client.post('/sage-integration/import/sales-csv', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  }

  // ==================== MODULE 2: SMART AUTO-ORDERING ====================

  async getAutoApprovalRules() {
    return this.client.get('/auto-ordering/rules');
  }
  async createAutoApprovalRule(data: any) {
    return this.client.post('/auto-ordering/rules', data);
  }
  async updateAutoApprovalRule(ruleId: string, data: any) {
    return this.client.put(`/auto-ordering/rules/${ruleId}`, data);
  }
  async deleteAutoApprovalRule(ruleId: string) {
    return this.client.delete(`/auto-ordering/rules/${ruleId}`);
  }
  async evaluateOrder(orderId: string) {
    return this.client.post(`/auto-ordering/evaluate/${orderId}`);
  }
  async autoApprovePendingOrders() {
    return this.client.post('/auto-ordering/auto-approve-pending');
  }
  async getAutoApprovalRate(days?: number) {
    return this.client.get('/auto-ordering/approval-rate', { params: { days } });
  }
  async getAutoApprovalLogs(params?: any) {
    return this.client.get('/auto-ordering/logs', { params });
  }

  // ==================== MODULE 3: ML FORECASTING ====================

  async autoSelectForecastModel(data: any) {
    return this.client.post('/ml-forecasting/auto-select', null, { params: data });
  }
  async getForecastAccuracyMetrics(params?: any) {
    return this.client.get('/ml-forecasting/accuracy', { params });
  }
  async getModelComparison(params?: any) {
    return this.client.get('/ml-forecasting/model-comparison', { params });
  }

  // ==================== MODULE 4: TESTER MANAGEMENT ====================

  async getTesterRatios(params?: any) {
    return this.client.get('/tester-management/ratios', { params });
  }
  async createTesterRatio(data: any) {
    return this.client.post('/tester-management/ratios', data);
  }
  async updateTesterRatio(ratioId: string, data: any) {
    return this.client.put(`/tester-management/ratios/${ratioId}`, data);
  }
  async deleteTesterRatio(ratioId: string) {
    return this.client.delete(`/tester-management/ratios/${ratioId}`);
  }
  async getTesterInventory(params?: any) {
    return this.client.get('/tester-management/inventory', { params });
  }
  async updateTesterStock(storeId: string, productId: string, data: any) {
    return this.client.put(`/tester-management/inventory/${storeId}/${productId}`, data);
  }
  async getTesterOrders(params?: any) {
    return this.client.get('/tester-management/orders', { params });
  }
  async generateTesterOrders() {
    return this.client.post('/tester-management/orders/generate');
  }
  async approveTesterOrder(orderId: string, quantity: number) {
    return this.client.post(`/tester-management/orders/${orderId}/approve`, { approved_quantity: quantity });
  }
  async bulkApproveTesterOrders(orderIds: string[]) {
    return this.client.post('/tester-management/orders/bulk-approve', { order_ids: orderIds });
  }
  async getTesterDashboard() {
    return this.client.get('/tester-management/dashboard');
  }
  async getTesterAlerts() {
    return this.client.get('/tester-management/alerts');
  }
  async getTesterNeeds() {
    return this.client.get('/tester-management/needs');
  }

  // ==================== MODULE 5: ABC CLASSIFICATION BY CATEGORY ====================

  async getAbcByCategory(params?: any) {
    return this.client.get('/abc-category/by-category', { params });
  }
  async getXyzClassification(params?: any) {
    return this.client.get('/abc-category/xyz', { params });
  }
  async getAbcXyzMatrix(params?: any) {
    return this.client.get('/abc-category/matrix', { params });
  }
  async getAbcCategorySummary(params?: any) {
    return this.client.get('/abc-category/category-summary', { params });
  }
  async getAbcSettings() {
    return this.client.get('/abc-category/settings');
  }
  async saveAbcSettings(data: {
    data_source: string;
    scope: string;
    thresholds: { A: number; B: number; C: number; D: number };
    brand_category_thresholds?: Record<string, Record<string, { A: number; B: number; C: number; D: number }>>;
    category_thresholds?: Record<string, { A: number; B: number; C: number; D: number }>;
  }) {
    return this.client.put('/abc-category/settings', data);
  }
  async previewAbcClassification(data: {
    thresholds: { A: number; B: number; C: number; D: number };
    data_source: string;
    scope: string;
    period_months: number;
    brand?: string;
    brand_category_thresholds?: Record<string, Record<string, { A: number; B: number; C: number; D: number }>>;
    category_thresholds?: Record<string, { A: number; B: number; C: number; D: number }>;
  }): Promise<{ data: {
    A: number; B: number; C: number; D: number; E: number; total: number;
    categories: Record<string, { A: number; B: number; C: number; D: number; E: number; total: number; total_revenue: number }>;
  } }> {
    return this.client.post('/abc-category/preview', data);
  }

  // ==================== MODULE 6: PROMOTION MANAGEMENT ====================

  async getPromoCalendar(params?: any) {
    return this.client.get('/promotion-management/calendar', { params });
  }
  async getPromoScenarios(eventId: string) {
    return this.client.get(`/promotion-management/events/${eventId}/scenarios`);
  }
  async createPromoScenario(eventId: string, data: any) {
    return this.client.post(`/promotion-management/events/${eventId}/scenarios`, data);
  }
  async updatePromoScenario(scenarioId: string, data: any) {
    return this.client.put(`/promotion-management/scenarios/${scenarioId}`, data);
  }
  async selectPromoScenario(scenarioId: string) {
    return this.client.post(`/promotion-management/scenarios/${scenarioId}/select`);
  }
  async comparePromoScenarios(eventId: string) {
    return this.client.get(`/promotion-management/events/${eventId}/compare`);
  }
  async estimatePromoLift(data: any) {
    return this.client.post('/promotion-management/estimate-lift', data);
  }
  async getPromoStockRequirements(eventId: string, scenarioId: string) {
    return this.client.post(`/promotion-management/events/${eventId}/stock-requirements`, { scenario_id: scenarioId });
  }
  async adaptForecastsForPromo(eventId: string) {
    return this.client.post(`/promotion-management/events/${eventId}/adapt-forecasts`);
  }
  async recordPromoResults(eventId: string, data: any) {
    return this.client.post(`/promotion-management/events/${eventId}/record-results`, data);
  }

  // ==================== MODULE 7: GWP MANAGEMENT ====================

  async getGWPCampaigns(params?: any) {
    return this.client.get('/gwp-management/campaigns', { params });
  }
  async createGWPCampaign(data: any) {
    return this.client.post('/gwp-management/campaigns', data);
  }
  async updateGWPCampaign(campaignId: string, data: any) {
    return this.client.put(`/gwp-management/campaigns/${campaignId}`, data);
  }
  async getGWPCampaignDetail(campaignId: string) {
    return this.client.get(`/gwp-management/campaigns/${campaignId}`);
  }
  async updateGWPStock(campaignId: string, storeId: string, data: any) {
    return this.client.put(`/gwp-management/campaigns/${campaignId}/stores/${storeId}/stock`, data);
  }
  async calculateGWPNeeds(campaignId: string) {
    return this.client.post(`/gwp-management/campaigns/${campaignId}/calculate-needs`);
  }
  async distributeGift(campaignId: string, storeId: string, quantity: number) {
    return this.client.post(`/gwp-management/campaigns/${campaignId}/stores/${storeId}/distribute`, { quantity });
  }
  async getGWPDashboard() {
    return this.client.get('/gwp-management/dashboard');
  }

  // ==================== MODULE 8: ADVANCED BI & REPORTS ====================

  async getLFLAnalysis(params?: any) {
    return this.client.get('/advanced-bi/lfl', { params });
  }
  async getGMROI(params?: any) {
    return this.client.get('/advanced-bi/gmroi', { params });
  }
  async getSupplierScorecards(params?: any) {
    return this.client.get('/advanced-bi/supplier-scorecards', { params });
  }
  async getLostSales(params?: any) {
    return this.client.get('/advanced-bi/lost-sales', { params });
  }
  async getSellThroughRate(params?: any) {
    return this.client.get('/advanced-bi/sell-through', { params });
  }

  // ==================== MODULE 9: BATCH/EXPIRY MANAGEMENT ====================

  async getBatches(params?: any) {
    return this.client.get('/batch-inventory/', { params });
  }
  async receiveBatch(data: any) {
    return this.client.post('/batch-inventory/', data);
  }
  async updateBatchStock(batchId: string, data: any) {
    return this.client.put(`/batch-inventory/${batchId}/stock`, data);
  }
  async getExpiringSoon(params?: any) {
    return this.client.get('/batch-inventory/expiring-soon', { params });
  }
  async getExpiredBatches(params?: any) {
    return this.client.get('/batch-inventory/expired', { params });
  }
  async writeOffBatch(batchId: string, data?: any) {
    return this.client.post(`/batch-inventory/${batchId}/write-off`, data);
  }
  async applyMarkdown(batchId: string, markdownPct: number) {
    return this.client.post(`/batch-inventory/${batchId}/markdown`, { markdown_pct: markdownPct });
  }
  async allocateFefo(data: any) {
    return this.client.post('/batch-inventory/allocate-fefo', data);
  }
  async updateExpiryMetrics() {
    return this.client.post('/batch-inventory/update-metrics');
  }
  async getWriteOffSummary(params?: any) {
    return this.client.get('/batch-inventory/write-off-summary', { params });
  }

  // AI Insights Methods
  async generateAIInsights(data: { scope: string; scope_id?: string; period_months?: number }) {
    return this.client.post('/analytics/ai-insights', data);
  }

}

export default new ApiService();
